// Configurações de ambiente via process.env.REACT_APP_*
import {
  jidIsPrivacyLid,
  jidLooksLikeWhatsAppPhone,
  looksLikePhoneOnlyLabel,
} from "./utils/phoneDisplay";
import type { ColaboradorCargoJson } from "./types/collaboratorCargo";
import { safeUserFacingMessage } from "./utils/safeUserFacingError";

export const SUPABASE_URL        = process.env.REACT_APP_SUPABASE_URL        || "";
export const SUPABASE_ANON_KEY   = process.env.REACT_APP_SUPABASE_ANON_KEY   || "";
export const SUPABASE_STORAGE_URL = process.env.REACT_APP_SUPABASE_STORAGE_URL || "";
export const SUPABASE_SERVICE_ROLE_KEY = process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY || "";

/** Chave de navegador (Maps JS) — restrita por domínio no Google Cloud. */
export const GOOGLE_MAPS_BROWSER_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_BROWSER_KEY || "";

/** @deprecated OAuth Google só no backend (`/api/google/*`). Não usar no front. */
export const GOOGLE_CLIENT_ID     = "";
/** @deprecated OAuth Google só no backend (`/api/google/*`). Não usar no front. */
export const GOOGLE_CLIENT_SECRET = "";
/** @deprecated OAuth Google só no backend (`/api/google/*`). Não usar no front. */
export const GOOGLE_REDIRECT_URI  = "";

// npm start: proxy /api/n8n (cookie no localhost). Produção: caminhos relativos;
// o nginx do container encaminha /webhook /api /events ao backend (crachá no mesmo site).
const isDev = process.env.NODE_ENV === "development";

export type DelivaiEnv = "development" | "staging" | "production";

export const DELIVAI_ENV = (
  process.env.REACT_APP_DELIVAI_ENV || (isDev ? "development" : "production")
) as DelivaiEnv;

/**
 * Ponto único de verdade sobre produção no front.
 * Gates sensíveis (consentimento/aceite obrigatório) devem usar isso para ligar só em produção.
 * Observação: NODE_ENV="production" também vale em staging (build Docker); por isso exigimos
 * também DELIVAI_ENV === "production".
 */
export function isProductionEnv(): boolean {
  return process.env.NODE_ENV === "production" && DELIVAI_ENV === "production";
}

/** Staging (VPS test) ou localhost — ambientes onde validamos UI jurídica antes do go-live. */
export function isStagingOrLocalEnv(): boolean {
  return DELIVAI_ENV === "staging" || DELIVAI_ENV === "development";
}

function trimTrailingSlash(url: string): string {
  return url.replace(/\/$/, "");
}

/** URL absoluta do backend (env). Em produção o browser não usa isto — nginx tubo. */
const bakedBackendUrl = trimTrailingSlash(
  process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || ""
);

/** Endereço que o browser chama. Vazio = mesmo host (prod). No dev = VPS test no .env.local. */
export const BACKEND_URL = isDev ? bakedBackendUrl : "";

const derivedWebhookBase = bakedBackendUrl ? `${bakedBackendUrl}/webhook/` : "";
const derivedEventsUrl = bakedBackendUrl ? `${bakedBackendUrl}/events` : "";

export const EVOLUTION_API_URL = "/api/evolution";
export const EVOLUTION_API_KEY  = process.env.REACT_APP_EVOLUTION_API_KEY  || "";

export const N8N_BASE_URL     = process.env.REACT_APP_N8N_BASE_URL     || "";
export const N8N_BASIC_AUTH   = process.env.REACT_APP_N8N_BASIC_AUTH   || "";
export const N8N_WEBHOOK_URL  = isDev ? "/api/n8n/" : "/webhook/";
export const N8N_AUTH_HEADER  = process.env.REACT_APP_N8N_AUTH_HEADER  || "";
export const N8N_EVENTS_URL   = isDev
  ? process.env.REACT_APP_N8N_EVENTS_URL || derivedEventsUrl || ""
  : "/events";

/**
 * Headers do painel (SEC-2 dual): Authorization legado só se ainda houver no env.
 * Preferir cookie HttpOnly + `credentials: "include"`.
 */
export function panelAuthHeaders(
  extra?: Record<string, string>
): Record<string, string> {
  const headers: Record<string, string> = { ...(extra || {}) };
  if (N8N_AUTH_HEADER) {
    headers.Authorization = N8N_AUTH_HEADER;
  }
  return headers;
}

/** Credentials padrão do painel — cookie de sessão atravessa proxy / CORS. */
export const PANEL_FETCH_CREDENTIALS: RequestCredentials = "include";

/** Token SSE `/events` — deve coincidir com `EVENT_GATEWAY_TOKEN_SECRET` do backend no EasyPanel. */
export const EVENT_GATEWAY_TOKEN = (
  process.env.REACT_APP_EVENT_GATEWAY_TOKEN || ""
).trim();

export const API_URL     = BACKEND_URL || process.env.REACT_APP_API_URL || "";

/** Alvo real do proxy em dev — usado para alerta anti-produção. */
const devProxyTarget =
  process.env.REACT_APP_N8N_WEBHOOK_URL || derivedWebhookBase || "";

if (
  isDev &&
  DELIVAI_ENV === "development" &&
  devProxyTarget &&
  /prod|delivai-prod/i.test(devProxyTarget)
) {
  console.error(
      "[DelivAI] Backend apontando para PRODUÇÃO em dev local. " +
      "Confira .env.local: use o backend **test** na VPS, não prod."
  );
}
export const APP_NAME    = process.env.REACT_APP_APP_NAME    || "DelivAI";
// APP_VERSION deve conter o hash do commit (github.sha) para verificação no CI/CD
// Esta variável é usada no teste do GitHub Actions para verificar se o build foi feito corretamente
export const APP_VERSION = process.env.REACT_APP_APP_VERSION || "1.1.0";

/**
 * Polling da lista na OrdersScreen (GET get-orders via n8n).
 * Realtime Supabase + eventos `newOrderCreated` não cobrem: pedido em outro aparelho, canal WhatsApp/n8n,
 * ou build sem REACT_APP_SUPABASE_URL/ANON_KEY. Sem polling, o lojista precisa atualizar a página manualmente.
 */
export const ORDERS_SCREEN_POLLING = {
  ENABLED: process.env.REACT_APP_DISABLE_ORDERS_LIST_POLLING !== "true",
  INTERVAL_MS: (() => {
    const n = parseInt(process.env.REACT_APP_ORDERS_LIST_POLL_INTERVAL_MS || "", 10);
    if (Number.isFinite(n) && n >= 3000) return n;
    return 8_000;
  })(),
};

/**
 * Override opcional do instalador DeliDesk (URL fixa).
 * Se vazio, o modal usa o endpoint público do backend (sempre a release mais nova do canal).
 */
export const PRINT_AGENT_WINDOWS_DOWNLOAD_URL = (
  process.env.REACT_APP_PRINT_AGENT_WINDOWS_DOWNLOAD_URL || ""
).trim();

/** Canal do instalador: sandbox (dev/staging) ou prod. */
export function getDelideskDownloadChannel(): "sandbox" | "prod" {
  return DELIVAI_ENV === "production" ? "prod" : "sandbox";
}

/** URL do backend que resolve o .exe mais recente (JSON ou ?redirect=1). */
export function getDelideskDownloadApiUrl(opts?: {
  channel?: "sandbox" | "prod";
  redirect?: boolean;
}): string {
  const channel = opts?.channel ?? getDelideskDownloadChannel();
  const q = new URLSearchParams({ channel });
  if (opts?.redirect) q.set("redirect", "1");
  const path = `/webhook/public/delidesk-download?${q.toString()}`;
  if (!isDev) return path;
  const base = BACKEND_URL.replace(/\/$/, "");
  if (!base) return "";
  return `${base}${path}`;
}

// Configuração da API Evolution
export const EVOLUTION_CONFIG = {
  // URL base da sua API Evolution
  BASE_URL: EVOLUTION_API_URL,

  // Token de autenticação global
  API_KEY: EVOLUTION_API_KEY,

  // Endpoints da API
  ENDPOINTS: {
    // Instâncias
    CREATE_INSTANCE: "/instance/create",
    DELETE_INSTANCE: "/instance/delete",
    FETCH_INSTANCES: "/instance/fetchInstances",

    // WhatsApp por instância (v2: /instance/qrcode pode não existir; usar connect e alternativas)
    QR_CODE: "/instance/qrcode",
    FETCH_QR_CODE: "/instance/fetchQrCode",
    INSTANCE_STATUS: "/instance/status",
    CONNECTION_STATE: "/instance/connectionState",
    INSTANCE_CONNECT: "/instance/connect",
    INSTANCE_DISCONNECT: "/instance/logout",

    // Chat por instância (v2 usa findMessages em /chat; v1 usa /message/find)
    CONVERSATIONS: "/chat/findAll",
    FIND_CHATS: "/chat/findChats",
    /** Fallback quando findChats retorna 500 (bug mediaUrl em algumas instâncias Evolution). */
    FIND_CONTACTS: "/chat/findContacts",
    FIND_MESSAGES: "/chat/findMessages",
    MESSAGES: "/message/find",
    FETCH_PROFILE_PICTURE: "/chat/fetchProfilePictureUrl",
    /** Evolution v2: POST body `{ readMessages: [{ remoteJid, fromMe, id }] }` */
    MARK_MESSAGE_READ: "/chat/markMessageAsRead",
    /** Evolution v2 Postman: `POST /chat/archiveChat/{instance}` — body `lastMessage[]`, `archive`, `chat`. */
    ARCHIVE_CHAT: "/chat/archiveChat",
    /** Evolution v2: `POST /chat/markChatUnread/{instance}` — body `lastMessage[]`, `chat`. */
    MARK_CHAT_UNREAD: "/chat/markChatUnread",
    /** Descriptografa mídia (.enc → base64 exibível). Doc Evolution: POST body `{ message: { key } }`. */
    GET_BASE64_FROM_MEDIA_MESSAGE: "/chat/getBase64FromMediaMessage",
    SEND_MESSAGE: "/message/sendText",
    SEND_MEDIA: "/message/sendMedia",

    // Webhook (configurar para n8n receber eventos em tempo real)
    WEBHOOK: "/webhook",
    SET_WEBHOOK: "/webhook/set",
    /** GET /webhook/find/{instance} */
    FIND_WEBHOOK: "/webhook/find",
    /** GET /settings/find/{instance} */
    FIND_SETTINGS: "/settings/find",
    /** POST /settings/set/{instance} */
    SET_SETTINGS: "/settings/set",
  },

  // Configurações de polling (atualização automática)
  POLLING: {
    ENABLED: true,
    INTERVAL: 5000, // 5 segundos
    MAX_RETRIES: 3,
  },
};

/**
 * O código da Evolution (`EventDto`) espera corpo **aninhado**: `{ "webhook": { "enabled", "url", "events", "byEvents", "base64", "headers" } }`.
 * Payload plano com `webhookByEvents` na raiz → **400 Bad Request**.
 * Se `REACT_APP_EVOLUTION_WEBHOOK_SNAKE_CASE=true`, usa formato plano legado v1 (snake_case).
 */
export const EVOLUTION_WEBHOOK_SET_USE_SNAKE_CASE =
  process.env.REACT_APP_EVOLUTION_WEBHOOK_SNAKE_CASE === "true";

function parseEvolutionApiErrorResponse(data: unknown, status: number): string {
  if (data == null) return `HTTP ${status}`;
  if (typeof data === "string" && data.trim()) return data.trim().slice(0, 400);
  if (typeof data !== "object") return `HTTP ${status}`;
  const o = data as Record<string, unknown>;
  const pick = (obj: Record<string, unknown>): string | null => {
    const m = obj.message;
    if (typeof m === "string" && m.trim()) return m.trim();
    const e = obj.error;
    if (typeof e === "string" && e.trim()) return e.trim();
    if (Array.isArray(m) && typeof m[0] === "string") return m[0];
    return null;
  };
  const direct = pick(o);
  if (direct) return direct.length > 400 ? `${direct.slice(0, 400)}…` : direct;
  const resp = o.response;
  if (resp && typeof resp === "object") {
    const nested = pick(resp as Record<string, unknown>);
    if (nested) return nested;
  }
  try {
    const j = JSON.stringify(data);
    return j.length > 350 ? `${j.slice(0, 350)}…` : j;
  } catch {
    return `HTTP ${status}`;
  }
}

function buildEvolutionWebhookSetPayload(
  enabled: boolean,
  url: string,
  webhookByEvents: boolean,
  webhookBase64: boolean,
  events: string[],
  extraHeaders?: Record<string, string>
): Record<string, unknown> {
  const eventsPayload = enabled ? events : [];
  if (EVOLUTION_WEBHOOK_SET_USE_SNAKE_CASE) {
    const body: Record<string, unknown> = {
      enabled,
      url: url.trim(),
      events: eventsPayload,
      webhook_by_events: webhookByEvents,
      webhook_base64: webhookBase64,
    };
    if (extraHeaders && Object.keys(extraHeaders).length > 0) {
      body.headers = extraHeaders;
    }
    return body;
  }
  const webhook: Record<string, unknown> = {
    enabled,
    url: url.trim(),
    events: eventsPayload,
    byEvents: webhookByEvents,
    base64: webhookBase64,
  };
  if (extraHeaders && Object.keys(extraHeaders).length > 0) {
    webhook.headers = extraHeaders;
  }
  return { webhook };
}

// Configuração do n8n Webhook
export const N8N_CONFIG = {
  // URL base do webhook n8n
  WEBHOOK_URL: N8N_WEBHOOK_URL,

  // Header de autenticação
  AUTH_HEADER: N8N_AUTH_HEADER,

  // Endpoints dos webhooks
  ENDPOINTS: {
    GET_CUSTOMER: "get-customer",
    UPDATE_CUSTOMER: "update-customer",
    // Evolution API endpoints
    FIND_CHATS: "find-chats",
    FIND_MESSAGES: "find-messages",
    SEND_TEXT: "send-text",
    SEND_MEDIA: "send-media",
    MARK_READ: "mark-read",
    // Parsing de pedido iFood para pedido interno (via IA)
    PARSE_IFOOD_INTERNAL_ORDER: "parse-ifood-internal-order",
    PARSE_STANDARD_INTERNAL_ORDER: "parse-standard-internal-order",
  },

  // Event Gateway (SSE/WebSocket)
  EVENT_GATEWAY: {
    URL: N8N_EVENTS_URL,
    TYPE: "SSE", // ou "WS" para WebSocket
  },
};

// Configuração do Supabase
export const SUPABASE_CONFIG = {
  // URL do seu projeto Supabase
  URL: SUPABASE_URL,

  // Supabase onde fica o storage das imagens (e também as tabelas de mensagens WhatsApp)
  STORAGE_URL: SUPABASE_STORAGE_URL,

  // Chave anônima (public key)
  ANON_KEY: SUPABASE_ANON_KEY,

  // Tabelas
  TABLES: {
    ORDERS: "pedidos", // tabela de pedidos
    CUSTOMERS: "clientes", // tabela de clientes (também usado para login)
    CLIENTS: "clientes", // tabela de clientes (usado para licenças)
    APPOINTMENTS: "agendamentos", // tabela de agendamentos
    LEADS: "leads", // tabela de leads
    ORDER_ITEMS: "pedido_itens", // itens dos pedidos
    DELIVERY_CLIENTS: "clientes_delivery", // clientes delivery
    DELIVERY_TOKENS: "tokens_senha", // tokens de senha
    EMPLOYEES: "funcionarios", // tabela de funcionários
    // WhatsApp: mesmas tabelas ficam no Supabase do storage (REACT_APP_SUPABASE_STORAGE_URL)
    CONVERSAS_WHATSAPP: "conversas_whatsapp",
    MENSAGENS_WHATSAPP: "mensagens_whatsapp",
  },
};

// Tipos TypeScript
export interface User {
  id: string;
  name: string;
  cnpj: string;
  email: string;
  token: string;
  /** Login por CPF (colaborador). */
  cpf?: string;
  /** Telefone do colaborador (dígitos), quando o login/refresh trouxer. */
  phone?: string;
  isCollaborator?: boolean;
  /** Cargo/permissões (jsonb) após parse — só colaborador. */
  cargoJson?: ColaboradorCargoJson | null;
  /** ID do registro em employees (login por CPF). */
  employeeId?: string;
  /** Colaborador com agenda profissional ligada. */
  agendaEnabled?: boolean;
  /** Diária cadastrada no cargo do entregador (acerto do dia). */
  diaria?: number;
}

export interface Order {
  id: number;
  customer_name: string;
  customer_phone: string;
  customer_address: string;
  total: number;
  payment_method: string;
  status: "analysis" | "production" | "ready";
  created_at: string;
  updated_at: string;
}

export interface Employee {
  id: number;
  nome: string;
  cpf: string;
  cargo: string;
  departamento: string;
  status: string;
  salario: number;
  data_admissao: string;
}

// Interfaces para Evolution API via n8n
export interface EvolutionChat {
  jid: string;
  /**
   * JIDs distintos que a Evolution expõe para o mesmo contato (ex.: `...@lid` + `...@s.whatsapp.net`).
   * Usado para buscar e unir o histórico; `jid` é o canônico preferencial (número real quando existir).
   */
  mergedRemoteJids?: string[];
  /**
   * Com LID, `jid` pode ser `...@lid` ou `...@l.id`. O número costuma vir em `remoteJidAlt`
   * no chat ou dentro de `lastMessage.key.remoteJidAlt` (findChats).
   */
  phoneJid?: string;
  title?: string;
  /** Nome do contato (push name) - prioridade para exibição */
  pushName?: string;
  name?: string;
  /** URL da foto de perfil (pode vir da API ou ser buscada) */
  profilePictureUrl?: string;
  unreadCount: number;
  lastMessage?: string;
  lastMessageTime?: string;
  /** Conversa arquivada (WhatsApp / Evolution findChats). */
  archived?: boolean;
  /** Chave Baileys da última mensagem — exigida por `archiveChat` / `markChatUnread` na Evolution API v2. */
  lastMessageKey?: { remoteJid: string; fromMe: boolean; id: string };
  isGroup: boolean;
  participants?: string[];
  /** Pausa individual (Auto Individual OFF) — lista vermelho claro. */
  botPaused?: boolean;
  /** ok | watch | urgent — faixa da lista (verde / amarelo / vermelho escuro). */
  botAttention?: "ok" | "watch" | "urgent";
}

export interface EvolutionMessage {
  waMessageId: string;
  jid: string;
  direction: "in" | "out";
  text?: string;
  media?: {
    type: string;
    url?: string;
    mimetype?: string;
    filename?: string;
    /** URL WhatsApp é criptografada (.enc) — usar {@link fetchEvolutionMediaAsDataUrl}, não `<img src>`. */
    requiresApiDecrypt?: boolean;
  };
  /** Chave Baileys para `getBase64FromMediaMessage` quando a mídia não vem em base64/URL exibível. */
  whatsAppMessageKey?: {
    id: string;
    remoteJid: string;
    fromMe: boolean;
  };
  timestamp: string;
  ack: "pending" | "sent" | "delivered" | "read" | "failed";
  isFromMe: boolean;
  /** Mensagem citada (reply) — contextInfo.quotedMessage do Baileys. */
  quoted?: {
    text?: string;
    fromMe?: boolean;
    mediaLabel?: string;
  };
}

export interface Conversation {
  id: string;
  name: string;
  phone: string;
  lastMessage: string;
  unreadCount: number;
  status: "active" | "pending" | "in-progress";
  jid?: string; // Evolution JID
}

export interface Message {
  id: string;
  text: string;
  timestamp: string;
  isFromMe: boolean;
  waMessageId?: string; // Evolution Message ID
  ack?: "pending" | "sent" | "delivered" | "read" | "failed";
}

// Eventos do Event Gateway
export interface EvolutionEvent {
  type:
    | "MESSAGE_UPSERT"
    | "ACK_UPDATE"
    | "READ_UPDATE"
    | "CHAT_UPSERT"
    | "SYNC_READY"
    | "INGEST_PROGRESS"
    | "MESSAGES_HYDRATED";
  instance: string;
  data: {
    chat?: EvolutionChat;
    message?: EvolutionMessage;
    ack?: string;
    chatCount?: number;
    messageCount?: number;
    processed?: number;
    total?: number;
    remoteJid?: string;
    count?: number;
  };
}

// Função para obter headers de autenticação
export function getAuthHeaders() {
  return {
    "Content-Type": "application/json",
    apikey: EVOLUTION_CONFIG.API_KEY,
  };
}

/** Painel de conversas: proxy Kotlin /api/evolution (dev via setupProxy; prod via nginx). */
function shouldUseEvolutionBackendProxy(): boolean {
  if (!isDev) return true;
  return Boolean(bakedBackendUrl?.trim() || BACKEND_URL?.trim());
}

/** Dev com proxy `/api/evolution`: setupProxy injeta apikey — o bundle não precisa da chave. */
function evolutionDirectReady(): boolean {
  const base = EVOLUTION_CONFIG.BASE_URL?.trim();
  if (!base) return false;
  if (isDev && base.startsWith("/api/evolution")) return true;
  return Boolean(EVOLUTION_CONFIG.API_KEY?.trim());
}

/** Fallback direto quando credenciais Evolution existem — preenche lacunas enquanto o backend hidrata. */
function canFetchEvolutionDirect(): boolean {
  return evolutionDirectReady();
}

function shouldUseEvolutionDirectFallback(): boolean {
  if (process.env.REACT_APP_EVOLUTION_DIRECT_FALLBACK === "false") return false;
  if (process.env.REACT_APP_EVOLUTION_DIRECT_FALLBACK === "true") {
    return canFetchEvolutionDirect();
  }
  // Dev: lista via Evolution direto (como antes); backend enriquece/arquiva.
  if (isDev) return canFetchEvolutionDirect();
  // Produção/staging: só proxy quando BACKEND_URL está definido — sem vazar API key no bundle.
  if (shouldUseEvolutionBackendProxy()) return false;
  return canFetchEvolutionDirect();
}

/** Base URL para /api/evolution/* do Kotlin — proxy local em dev evita CORS. */
function evolutionKotlinBaseUrl(): string {
  if (isDev && BACKEND_URL?.trim()) return "/api/delivai-backend";
  return BACKEND_URL.replace(/\/$/, "");
}

const EVOLUTION_BACKEND_TIMEOUT_MS = 25_000;
/** Alinhado ao WebAsyncTask do Kotlin (25s) — 4s abortava antes do sync responder. */
const EVOLUTION_BACKEND_CHATS_TIMEOUT_MS = EVOLUTION_BACKEND_TIMEOUT_MS;

async function evolutionBackendRequest(
  method: "GET" | "POST" | "DELETE",
  path: string,
  opts?: {
    params?: Record<string, string | number | boolean | undefined | null>;
    body?: Record<string, unknown>;
    timeoutMs?: number;
    /** Backend do ambiente da visão Evolution (test|prod). */
    backendBaseUrl?: string;
  }
): Promise<unknown> {
  const base = (opts?.backendBaseUrl || evolutionKotlinBaseUrl()).replace(/\/$/, "");
  const qs = new URLSearchParams();
  if (opts?.params) {
    for (const [k, v] of Object.entries(opts.params)) {
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        qs.append(k, String(v));
      }
    }
  }
  const query = qs.toString();
  const url = `${base}${path}${query ? `?${query}` : ""}`;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (N8N_AUTH_HEADER) headers.Authorization = N8N_AUTH_HEADER;
  if (opts?.body) headers["Content-Type"] = "application/json";
  const controller = new AbortController();
  const timeoutMs = opts?.timeoutMs ?? EVOLUTION_BACKEND_TIMEOUT_MS;
  const timeoutHandle = setTimeout(
    () => controller.abort(),
    timeoutMs
  );
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      credentials: PANEL_FETCH_CREDENTIALS,
      signal: controller.signal,
      body: opts?.body ? JSON.stringify(opts.body) : undefined,
    });
  } catch (err) {
    clearTimeout(timeoutHandle);
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error(`Timeout (${timeoutMs}ms) ao chamar ${method} ${path}`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutHandle);
  }
  const text = await res.text();
  let data: unknown = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }
  if (!res.ok) {
    const obj =
      data && typeof data === "object" ? (data as Record<string, unknown>) : null;
    const detail =
      obj && typeof obj.message === "string" ? obj.message.trim() : "";
    const code = obj && typeof obj.error === "string" ? obj.error.trim() : "";
    const msg =
      detail && detail !== code ? detail : code || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

function mergeArchivedChatFlag(
  prev?: boolean,
  incoming?: boolean
): boolean {
  if (incoming === true) return true;
  if (incoming === false) return false;
  return Boolean(prev);
}

// Função para obter headers do n8n
export function getN8nHeaders() {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (N8N_CONFIG.AUTH_HEADER) {
    headers.Authorization = N8N_CONFIG.AUTH_HEADER;
  }
  return headers;
}

// Função para obter headers do Supabase
export function getSupabaseHeaders() {
  return {
    "Content-Type": "application/json",
    apikey: SUPABASE_CONFIG.ANON_KEY,
    Authorization: `Bearer ${SUPABASE_CONFIG.ANON_KEY}`,
  };
}

// Função para construir URL da API
export function buildApiUrl(
  endpoint: string,
  params: Record<string, string> = {}
) {
  let url = EVOLUTION_CONFIG.BASE_URL + endpoint;

  // Substituir parâmetros na URL
  Object.keys(params).forEach((key) => {
    url = url.replace(`{${key}}`, params[key]);
  });

  return url;
}

// Função para construir URL do n8n webhook
export function buildN8nUrl(endpoint: string) {
  return `${N8N_CONFIG.WEBHOOK_URL}${endpoint}`;
}

// Função para obter token JWT para Event Gateway
export function getEventToken(): string | null {
  return localStorage.getItem("evolution_event_token");
}

/** Token efetivo para SSE — storage > env de build > fallback dev-only. */
export function resolveEventGatewayToken(fallbackToken?: string | null): string | null {
  const stored = getEventToken();
  if (stored?.trim()) return stored.trim();
  if (fallbackToken?.trim()) return fallbackToken.trim();
  if (EVENT_GATEWAY_TOKEN) return EVENT_GATEWAY_TOKEN;
  if (process.env.NODE_ENV === "development") return "delivai";
  return null;
}

// Função para salvar token JWT para Event Gateway
export function setEventToken(token: string) {
  localStorage.setItem("evolution_event_token", token);
}

// Função para obter instância atual (Evolution API usa o CNPJ como nome da instância)
export function getCurrentInstance(): string | null {
  const user = getCurrentUser();
  const cnpj = user?.cnpj;
  if (!cnpj) return null;
  const cleaned = cleanCnpjFormat(cnpj);
  return cleaned.length === 14 ? cleaned : null;
}

/**
 * Em produção a Evolution (ou proxies) às vezes serializa `lastMessage` como string JSON.
 * Sem isto, perdemos `key.remoteJidAlt` e chats `...@lid` não fundem com `...@s.whatsapp.net`.
 */
function unwrapLastMessageForExtraction(raw: unknown): unknown {
  if (raw == null) return raw;
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t.startsWith("{") && t.endsWith("}")) {
      try {
        return JSON.parse(t) as unknown;
      } catch {
        return raw;
      }
    }
  }
  return raw;
}

/** Desembrulha viewOnce / ephemeral / documentWithCaption. */
function unwrapBaileysMessagePayload(msg: Record<string, unknown>): Record<string, unknown> {
  const viewOnce = msg.viewOnceMessage as Record<string, unknown> | undefined;
  if (viewOnce?.message && typeof viewOnce.message === "object") {
    return viewOnce.message as Record<string, unknown>;
  }
  const ephemeral = msg.ephemeralMessage as Record<string, unknown> | undefined;
  if (ephemeral?.message && typeof ephemeral.message === "object") {
    return ephemeral.message as Record<string, unknown>;
  }
  const dwc = msg.documentWithCaptionMessage as Record<string, unknown> | undefined;
  if (dwc?.message && typeof dwc.message === "object") {
    return dwc.message as Record<string, unknown>;
  }
  return msg;
}

function extractBaileysContextInfo(
  msg: Record<string, unknown>
): Record<string, unknown> | undefined {
  const inner = unwrapBaileysMessagePayload(msg);
  const carriers = [
    "extendedTextMessage",
    "imageMessage",
    "videoMessage",
    "documentMessage",
    "audioMessage",
    "buttonsMessage",
    "templateMessage",
    "interactiveMessage",
    "listMessage",
    "stickerMessage",
  ];
  for (const key of carriers) {
    const block = inner[key] as Record<string, unknown> | undefined;
    if (block?.contextInfo && typeof block.contextInfo === "object") {
      return block.contextInfo as Record<string, unknown>;
    }
  }
  return undefined;
}

function extractQuotedFromContext(
  contextInfo: Record<string, unknown> | undefined
): EvolutionMessage["quoted"] | undefined {
  if (!contextInfo) return undefined;
  const raw = contextInfo.quotedMessage;
  if (!raw || typeof raw !== "object") return undefined;
  const q = raw as Record<string, unknown>;
  const inner = unwrapBaileysMessagePayload(q);
  const text = extractTextFromBaileysMessage(inner, { allowMediaLabels: true });
  let mediaLabel: string | undefined;
  if (inner.imageMessage) mediaLabel = "Foto";
  else if (inner.videoMessage) mediaLabel = "Vídeo";
  else if (inner.audioMessage) mediaLabel = "Áudio";
  else if (inner.stickerMessage) mediaLabel = "Figurinha";
  else if (inner.documentMessage) {
    const dm = inner.documentMessage as Record<string, unknown>;
    const fn = typeof dm.fileName === "string" ? dm.fileName.trim() : "";
    mediaLabel = fn || "Documento";
  }
  if (!text && !mediaLabel) return undefined;
  const participant =
    typeof contextInfo.participant === "string" ? contextInfo.participant : "";
  const fromMe =
    typeof contextInfo.remoteJid === "string"
      ? false
      : participant.includes("@g.us")
        ? false
        : undefined;
  return { text: text || mediaLabel, fromMe, mediaLabel };
}

/** Texto legível de payloads Baileys (incl. templates iFood / botões). */
function extractTextFromBaileysMessage(
  msg: Record<string, unknown>,
  opts?: { allowMediaLabels?: boolean }
): string | undefined {
  const inner = unwrapBaileysMessagePayload(msg);
  const allowLabels = opts?.allowMediaLabels === true;

  if (typeof inner.conversation === "string" && inner.conversation.trim()) {
    return inner.conversation.trim();
  }
  const ext = inner.extendedTextMessage as Record<string, unknown> | undefined;
  if (ext && typeof ext.text === "string" && ext.text.trim()) {
    return ext.text.trim();
  }

  const tpl = inner.templateMessage as Record<string, unknown> | undefined;
  if (tpl) {
    const hydrated = tpl.hydratedTemplate as Record<string, unknown> | undefined;
    if (hydrated) {
      const parts = [
        hydrated.hydratedTitleText,
        hydrated.hydratedContentText,
        hydrated.hydratedFooterText,
      ]
        .map((v) => (typeof v === "string" ? v.trim() : ""))
        .filter(Boolean);
      if (parts.length > 0) return parts.join("\n");
    }
    const fourRow = tpl.fourRowTemplate as Record<string, unknown> | undefined;
    if (fourRow && typeof fourRow.contentText === "string" && fourRow.contentText.trim()) {
      return fourRow.contentText.trim();
    }
  }

  const buttons = inner.buttonsMessage as Record<string, unknown> | undefined;
  if (buttons) {
    for (const k of ["contentText", "text", "footerText", "headerText"]) {
      const v = buttons[k];
      if (typeof v === "string" && v.trim()) return v.trim();
    }
  }

  const interactive = inner.interactiveMessage as Record<string, unknown> | undefined;
  if (interactive) {
    const body = interactive.body as Record<string, unknown> | undefined;
    if (body && typeof body.text === "string" && body.text.trim()) return body.text.trim();
    const header = interactive.header as Record<string, unknown> | undefined;
    if (header && typeof header.title === "string" && header.title.trim()) {
      return header.title.trim();
    }
  }

  const list = inner.listMessage as Record<string, unknown> | undefined;
  if (list) {
    for (const k of ["description", "title", "buttonText"]) {
      const v = list[k];
      if (typeof v === "string" && v.trim()) return v.trim();
    }
  }

  const im = inner.imageMessage as Record<string, unknown> | undefined;
  if (im) {
    if (typeof im.caption === "string" && im.caption.trim()) return im.caption.trim();
    if (allowLabels) return "Foto";
    return undefined;
  }
  const vm = inner.videoMessage as Record<string, unknown> | undefined;
  if (vm) {
    if (typeof vm.caption === "string" && vm.caption.trim()) return vm.caption.trim();
    if (allowLabels) return "Vídeo";
    return undefined;
  }
  const dm = inner.documentMessage as Record<string, unknown> | undefined;
  if (dm) {
    if (typeof dm.caption === "string" && dm.caption.trim()) return dm.caption.trim();
    const fn = typeof dm.fileName === "string" ? dm.fileName.trim() : "";
    if (fn) return fn;
    if (allowLabels) return "Documento";
    return undefined;
  }
  if (inner.audioMessage) return allowLabels ? "Áudio" : undefined;
  if (inner.stickerMessage) return allowLabels ? "Figurinha" : undefined;
  if (inner.contactMessage) return allowLabels ? "Contato" : undefined;
  if (inner.locationMessage) return allowLabels ? "Localização" : undefined;

  return undefined;
}

/** Conteúdo textual de um `message` Baileys (incl. mídia sem legenda) para preview na lista. */
function previewFromBaileysMessageRecord(msg: Record<string, unknown>): string | undefined {
  return extractTextFromBaileysMessage(msg, { allowMediaLabels: true });
}

/** Texto de preview quando `lastMessage` vem como objeto (proto Baileys) ou JSON em string. */
function extractLastMessagePreview(raw: unknown): string | undefined {
  const un = unwrapLastMessageForExtraction(raw);
  if (un == null) return undefined;
  if (typeof un === "string") return un.trim() || undefined;
  if (typeof un !== "object") return undefined;
  const o = un as Record<string, unknown>;
  if (typeof o.conversation === "string" && o.conversation.trim()) {
    return o.conversation.trim();
  }
  if (o.message && typeof o.message === "object") {
    const msg = o.message as Record<string, unknown>;
    const p = previewFromBaileysMessageRecord(msg);
    if (p) return p;
  }
  const top = previewFromBaileysMessageRecord(o);
  if (top) return top;
  return undefined;
}

/** Preview de última mensagem a partir do payload bruto da API (lista de chats). */
export function lastMessagePreviewFromRaw(raw: unknown): string {
  return extractLastMessagePreview(raw) ?? "";
}

function evolutionMessageSortTime(m: EvolutionMessage): number {
  if (!m.timestamp) return 0;
  const t = new Date(m.timestamp).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Timestamp comparável para ordenar mensagens e previews. */
export function evolutionMessageTimestampMs(m: EvolutionMessage): number {
  return evolutionMessageSortTime(m);
}

/** Mensagem mais recente — a lista pode estar fora de ordem após merges no cache. */
export function newestEvolutionMessageInList(
  msgs: EvolutionMessage[]
): EvolutionMessage | undefined {
  if (msgs.length === 0) return undefined;
  let best = msgs[0];
  let bestT = evolutionMessageSortTime(best);
  for (let i = 1; i < msgs.length; i++) {
    const t = evolutionMessageSortTime(msgs[i]);
    if (t >= bestT) {
      best = msgs[i];
      bestT = t;
    }
  }
  return best;
}

const SIDEBAR_PLACEHOLDER_TEXT = new Set([
  "[Imagem]",
  "[Figurinha]",
  "[Vídeo]",
  "[Áudio]",
  "[Documento]",
  "[Contato]",
  "[Localização]",
]);

/** Uma linha para a lista lateral: última mensagem (texto ou tipo de mídia). */
export function sidebarPreviewFromEvolutionMessage(m: EvolutionMessage): string {
  const raw = m.text;
  let t = "";
  if (typeof raw === "string") t = raw.trim();
  else if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    if (typeof o.conversation === "string") t = o.conversation.trim();
    else if (
      o.extendedTextMessage &&
      typeof (o.extendedTextMessage as Record<string, unknown>).text === "string"
    ) {
      t = String((o.extendedTextMessage as Record<string, unknown>).text).trim();
    } else if (o.message && typeof o.message === "object") {
      const inner = previewFromBaileysMessageRecord(o.message as Record<string, unknown>);
      if (inner) t = inner;
    }
  }
  if (t && !SIDEBAR_PLACEHOLDER_TEXT.has(t)) return t;
  const mt = m.media?.type;
  if (mt === "image") return "Foto";
  if (mt === "sticker") return "Figurinha";
  if (mt === "video") return "Vídeo";
  if (mt === "audio") return "Áudio";
  if (mt === "document") {
    const fn = m.media?.filename?.trim();
    return fn || "Documento";
  }
  if (t) return t;
  return "";
}

/**
 * Nome exibível do chat (Evolution/Baileys).
 * Nome guardado na agenda (`name`, `contact.name`, …) tem prioridade sobre `pushName` (nome público no WhatsApp).
 */
function pickChatDisplayName(c: Record<string, unknown>): string | undefined {
  const tryStr = (v: unknown) =>
    v != null && String(v).trim() ? String(v).trim() : undefined;

  const contact = c.contact;
  const co =
    contact && typeof contact === "object"
      ? (contact as Record<string, unknown>)
      : null;

  const fromContactSaved = (x: Record<string, unknown>) =>
    tryStr(
      x.name ??
        x.notifyName ??
        (x as { displayName?: unknown }).displayName
    );

  const saved =
    tryStr(c.name) ||
    tryStr(c.contactName) ||
    (co ? fromContactSaved(co) : undefined) ||
    tryStr(c.verifiedName) ||
    (co ? tryStr(co.verifiedName) : undefined);

  const publicOrFallback =
    tryStr(c.pushName) ||
    (co ? tryStr(co.pushName) : undefined) ||
    tryStr(c.title);

  return saved || publicOrFallback || undefined;
}

/** findChats: número real costuma vir em `lastMessage.key.remoteJidAlt`, não no raiz do chat. */
function extractRemoteJidAltFromLastMessage(raw: unknown): string | undefined {
  const un = unwrapLastMessageForExtraction(raw);
  if (!un || typeof un !== "object") return undefined;
  const o = un as Record<string, unknown>;
  const key = o.key;
  if (!key || typeof key !== "object") return undefined;
  const alt = (key as Record<string, unknown>).remoteJidAlt;
  return typeof alt === "string" && alt.trim() ? alt.trim() : undefined;
}

/**
 * findChats: `pushName` no topo pode ser null; o nome vem em `lastMessage.pushName`.
 * Ignora última mensagem `fromMe` — nesse caso o pushName é o perfil da loja
 * (ex.: "Sushi Kamikaze"), não o nome do cliente.
 */
function extractPushNameFromLastMessage(raw: unknown): string | undefined {
  const un = unwrapLastMessageForExtraction(raw);
  if (!un || typeof un !== "object") return undefined;
  const o = un as Record<string, unknown>;
  const key = o.key && typeof o.key === "object" ? (o.key as Record<string, unknown>) : null;
  const fromMeRaw = key?.fromMe ?? o.fromMe;
  const fromMe =
    fromMeRaw === true ||
    fromMeRaw === 1 ||
    fromMeRaw === "1" ||
    (typeof fromMeRaw === "string" && fromMeRaw.toLowerCase() === "true");
  if (fromMe) return undefined;

  const tryFrom = (obj: Record<string, unknown>): string | undefined => {
    if (typeof obj.pushName === "string" && obj.pushName.trim()) return obj.pushName.trim();
    const low = (obj as { pushname?: string }).pushname;
    if (typeof low === "string" && low.trim()) return low.trim();
    return undefined;
  };
  const top = tryFrom(o);
  if (top) return top;
  if (key) {
    const fromKey = tryFrom(key);
    if (fromKey) return fromKey;
  }
  return undefined;
}

/**
 * Evolution / Baileys costuma expor o chat em `remoteJid`; `jid`/`id` nem sempre vêm preenchidos.
 * Sem JID válido, o front agrupa cache e fetch de mensagens no mesmo bucket (histórico duplicado).
 */
function extractChatRemoteJid(c: Record<string, unknown>): string {
  const t = (v: unknown): string =>
    v != null && String(v).trim() ? String(v).trim() : "";

  let raw =
    t(c.remoteJid) ||
    t(c.remote) ||
    t(c.jid) ||
    t(c.id);

  const key = c.key;
  if (!raw && key && typeof key === "object") {
    const ko = key as Record<string, unknown>;
    raw = t(ko.remoteJid) || t(ko.remote);
  }

  if (!raw) return "";

  if (raw.includes("@")) {
    return raw;
  }

  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";

  return `${digits}@s.whatsapp.net`;
}

function digitsOnlyBeforeAtInJid(jid: string): string {
  const part = (jid || "").split("@")[0] || "";
  return part.replace(/\D/g, "");
}

/** JID de telefone canónico para `phoneJid` ao coagir payload Evolution. */
function normalizeCoercePhoneJid(jid: string): string | undefined {
  const j = jid.trim();
  if (!j) return undefined;
  const lower = j.toLowerCase();
  if (lower.endsWith("@c.us")) {
    const d = digitsOnlyBeforeAtInJid(j);
    return d ? `${d}@s.whatsapp.net` : undefined;
  }
  if (jidLooksLikeWhatsAppPhone(j) && !jidIsPrivacyLid(j)) return j;
  return undefined;
}

/** Telefone real em `lastMessage.key` (útil quando o chat está em `...@lid`). */
function extractPhoneJidFromLastMessageKey(raw: unknown): string | undefined {
  const un = unwrapLastMessageForExtraction(raw);
  if (!un || typeof un !== "object") return undefined;
  const key = (un as Record<string, unknown>).key;
  if (!key || typeof key !== "object") return undefined;
  const k = key as Record<string, unknown>;
  const alt = typeof k.remoteJidAlt === "string" ? k.remoteJidAlt.trim() : "";
  if (alt) {
    const n = normalizeCoercePhoneJid(alt);
    if (n) return n;
  }
  const rj = typeof k.remoteJid === "string" ? k.remoteJid.trim() : "";
  if (rj) {
    const n = normalizeCoercePhoneJid(rj);
    if (n) return n;
  }
  return undefined;
}

/** Extrai `key` da última mensagem (findChats) para endpoints `archiveChat` / `markChatUnread`. */
function extractLastMessageKeyFromRaw(
  raw: unknown,
  fallbackJid: string
): EvolutionChat["lastMessageKey"] | undefined {
  const un = unwrapLastMessageForExtraction(raw);
  if (!un || typeof un !== "object") return undefined;
  const o = un as Record<string, unknown>;
  const key = o.key;
  if (!key || typeof key !== "object") return undefined;
  const k = key as Record<string, unknown>;
  let id = "";
  if (typeof k.id === "string") id = k.id.trim();
  else if (k.id != null && typeof k.id === "object") {
    const ido = k.id as Record<string, unknown>;
    if (typeof ido.id === "string") id = ido.id.trim();
  }
  if (!id) return undefined;
  const remoteJid =
    typeof k.remoteJid === "string" && k.remoteJid.trim()
      ? k.remoteJid.trim()
      : fallbackJid;
  return {
    remoteJid,
    fromMe: Boolean(k.fromMe),
    id,
  };
}

/** Normaliza dígitos para agrupar o mesmo telefone (ex.: BR com/sem 55). */
function normalizeDedupePhoneDigits(digits: string): string | null {
  let x = digits.replace(/\D/g, "");
  if (!x || x.length < 10) return null;
  if (x.length <= 11 && !x.startsWith("55")) {
    x = `55${x}`;
  }
  return x;
}

/**
 * Chave estável para fundir conversas 1:1 duplicadas (mesmo número, JIDs diferentes).
 * Grupos e LID sem `phoneJid` numérico retornam null (não agrupa).
 */
function phoneDedupeKeyForChat(c: EvolutionChat): string | null {
  if (c.isGroup) return null;
  const j = (c.jid || "").toLowerCase();
  if (j.endsWith("@g.us")) return null;

  if (c.phoneJid && jidLooksLikeWhatsAppPhone(c.phoneJid)) {
    const k = normalizeDedupePhoneDigits(digitsOnlyBeforeAtInJid(c.phoneJid));
    if (k) return k;
  }

  if (jidLooksLikeWhatsAppPhone(c.jid) && !jidIsPrivacyLid(c.jid)) {
    const k = normalizeDedupePhoneDigits(digitsOnlyBeforeAtInJid(c.jid));
    if (k) return k;
  }

  if (jidIsPrivacyLid(c.jid)) {
    const hint = extractPhoneJidFromLastMessageKey(c.lastMessage);
    if (hint) {
      const k = normalizeDedupePhoneDigits(digitsOnlyBeforeAtInJid(hint));
      if (k) return k;
    }
    if (c.phoneJid && jidLooksLikeWhatsAppPhone(c.phoneJid)) {
      const k = normalizeDedupePhoneDigits(digitsOnlyBeforeAtInJid(c.phoneJid));
      if (k) return k;
    }
  }

  return null;
}

/** Resolve chave de telefone para @lid usando lastMessage quando `phoneDedupeKeyForChat` falha. */
function resolvePrivacyLidPhoneKey(c: EvolutionChat): string | null {
  if (!jidIsPrivacyLid(c.jid || "")) return null;
  const hint = extractPhoneJidFromLastMessageKey(c.lastMessage);
  if (!hint) return null;
  return normalizeDedupePhoneDigits(digitsOnlyBeforeAtInJid(hint)) || null;
}

function primaryLabelForChat(c: EvolutionChat): string {
  const name = c.name ?? c.pushName ?? c.title;
  return name != null ? String(name).trim() : "";
}

/** Candidatos explícitos — agenda (`name`) antes do pushName público. */
function collectLabelCandidatesFromChat(c: EvolutionChat): string[] {
  const out: string[] = [];
  const add = (v: unknown) => {
    const s = v != null ? String(v).trim() : "";
    if (s) out.push(s);
  };
  add(c.name);
  add(c.title);
  add(c.pushName);
  return out;
}

function isWeakChatDisplayName(s: string): boolean {
  const t = s.trim().toLowerCase();
  if (!t) return true;
  if (t === "você" || t === "voce" || t === "you") return true;
  if (looksLikePhoneOnlyLabel(s)) return true;
  if (/^[\s.:·∴\-–—_]+$/.test(s.trim())) return true;
  if (t.length <= 2 && !/[a-záàãâéêíóôõúç]/i.test(t)) return true;
  return false;
}

function isVoceLabel(s: string): boolean {
  const t = s.trim().toLowerCase();
  return t === "você" || t === "voce" || t === "you";
}

/** Melhor nome entre vários chats fundidos: nunca prioriza "Você" se existir outro rótulo. */
function pickBestLabelFromChatGroup(group: EvolutionChat[]): string | undefined {
  const labels: string[] = [];
  for (const c of group) {
    for (const lab of collectLabelCandidatesFromChat(c)) {
      if (lab.trim()) labels.push(lab.trim());
    }
  }
  if (labels.length === 0) return undefined;

  const unique = [...new Set(labels)];
  const pickLongest = (arr: string[]) =>
    arr.reduce((a, b) => (a.length >= b.length ? a : b));

  const strong = unique.filter((l) => !isWeakChatDisplayName(l));
  if (strong.length > 0) return pickLongest(strong);

  const notVoce = unique.filter(
    (l) => !isVoceLabel(l) && !looksLikePhoneOnlyLabel(l)
  );
  if (notVoce.length > 0) return pickLongest(notVoce);

  return undefined;
}

/**
 * Ao fundir dois registos com o mesmo JID, não use `c.pushName || prev.pushName`:
 * o segundo item pode trazer só "Você" e apagar "BIANCA | ..." do primeiro.
 */
function mergeChatLabelsForDuplicateJid(
  prev: EvolutionChat,
  next: EvolutionChat
): Pick<EvolutionChat, "pushName" | "name" | "title"> {
  const best = pickBestLabelFromChatGroup([prev, next]);
  if (best) {
    return {
      pushName: best,
      name: best,
      title: next.title || prev.title,
    };
  }
  const preferNonVoce = (a?: string, b?: string): string | undefined => {
    const candidates = [a, b].filter(
      (v): v is string => v != null && String(v).trim() !== ""
    );
    const good = candidates.find(
      (s) => !isWeakChatDisplayName(s) && !isVoceLabel(s)
    );
    return good ?? candidates[0];
  };
  return {
    pushName: preferNonVoce(next.pushName, prev.pushName),
    name: preferNonVoce(next.name, prev.name),
    title: next.title || prev.title,
  };
}

/**
 * Atualiza um chat (ex.: CHAT_UPSERT) sem deixar `incoming` com "Você" apagar o nome já conhecido.
 */
export function mergeEvolutionChatUpsert(
  prev: EvolutionChat,
  incoming: EvolutionChat
): EvolutionChat {
  const labels = mergeChatLabelsForDuplicateJid(prev, incoming);
  const prevActivity = evolutionChatLastActivityMs(prev);
  const incomingActivity = evolutionChatLastActivityMs(incoming);
  const incomingPreviewEmpty =
    incoming.lastMessage == null ||
    String(incoming.lastMessage).trim() === "";
  const keepPrevPreview =
    Boolean(prev.lastMessage) &&
    (prevActivity > incomingActivity ||
      (incomingPreviewEmpty && prevActivity >= incomingActivity));

  return {
    ...prev,
    ...incoming,
    pushName: labels.pushName ?? incoming.pushName ?? prev.pushName,
    name: labels.name ?? incoming.name ?? prev.name,
    title: labels.title ?? incoming.title ?? prev.title,
    lastMessage: keepPrevPreview
      ? prev.lastMessage
      : (incoming.lastMessage ?? prev.lastMessage),
    lastMessageTime: keepPrevPreview
      ? prev.lastMessageTime ?? incoming.lastMessageTime
      : (incoming.lastMessageTime ?? prev.lastMessageTime),
    lastMessageKey: incoming.lastMessageKey ?? prev.lastMessageKey,
    archived: mergeArchivedChatFlag(prev.archived, incoming.archived),
    unreadCount: Math.max(prev.unreadCount ?? 0, incoming.unreadCount ?? 0),
    botPaused: incoming.botPaused ?? prev.botPaused,
    botAttention: incoming.botAttention ?? prev.botAttention,
  };
}

/** Mantém preview/hora mais recentes do estado ao refrescar findChats. */
export function mergeFetchedConversationPreviews(
  prev: EvolutionChat[],
  fetched: EvolutionChat[]
): EvolutionChat[] {
  const prevByJid = new Map<string, EvolutionChat>();
  for (const c of prev) {
    prevByJid.set(c.jid, c);
    for (const j of c.mergedRemoteJids ?? []) {
      if (j) prevByJid.set(j, c);
    }
  }

  return fetched.map((f) => {
    const existing = prevByJid.get(f.jid);
    if (!existing) return f;
    const prevMs = evolutionChatLastActivityMs(existing);
    const fetchMs = evolutionChatLastActivityMs(f);
    const merged = mergeEvolutionChatUpsert(existing, f);
    if (fetchMs >= prevMs) {
      return {
        ...merged,
        unreadCount: Math.max(
          existing.unreadCount ?? 0,
          f.unreadCount ?? 0,
          merged.unreadCount ?? 0
        ),
      };
    }
    return {
      ...merged,
      lastMessage: existing.lastMessage ?? f.lastMessage,
      lastMessageTime: existing.lastMessageTime ?? f.lastMessageTime,
      lastMessageKey: existing.lastMessageKey ?? f.lastMessageKey,
      unreadCount: Math.max(existing.unreadCount ?? 0, f.unreadCount ?? 0),
    };
  });
}

export function evolutionChatLastActivityMs(c: EvolutionChat): number {
  const t = c.lastMessageTime;
  if (!t) return 0;
  const ms = new Date(t).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/** Lê contador de não lidas em todos os formatos conhecidos da Evolution/Baileys. */
export function parseEvolutionUnreadCount(raw: Record<string, unknown>): number {
  const nested =
    raw.chat && typeof raw.chat === "object"
      ? (raw.chat as Record<string, unknown>)
      : null;
  const candidates = [
    raw.unreadCount,
    raw.unreadMessages,
    raw.unread,
    nested?.unreadCount,
    nested?.unreadMessages,
    nested?.unread,
  ];
  for (const u of candidates) {
    const n = Number(u);
    if (Number.isFinite(n) && n >= 0) return Math.min(Math.floor(n), 999);
  }
  return 0;
}

function parseBotPausedFlag(raw: Record<string, unknown>): boolean | undefined {
  const v = raw.botPaused ?? raw.bot_paused;
  if (v === true || v === "true" || v === 1) return true;
  if (v === false || v === "false" || v === 0) return false;
  return undefined;
}

function parseBotAttentionFlag(
  raw: Record<string, unknown>
): EvolutionChat["botAttention"] | undefined {
  const v = String(raw.botAttention ?? raw.bot_attention ?? "").trim().toLowerCase();
  if (v === "watch" || v === "urgent" || v === "ok") return v;
  return undefined;
}

/**
 * Quantas mensagens recebidas existem depois do marcador de leitura (mais recentes).
 * `readUpToId` = última mensagem recebida já vista/lida pelo operador.
 */
export function countUnreadInboundSince(
  messages: EvolutionMessage[],
  readUpToId: string | null | undefined
): number {
  if (!messages.length) return 0;
  const sorted = [...messages].sort(
    (a, b) => evolutionMessageTimestampMs(a) - evolutionMessageTimestampMs(b)
  );
  let count = 0;
  for (let i = sorted.length - 1; i >= 0; i--) {
    const m = sorted[i];
    if (m.isFromMe) continue;
    if (!m.waMessageId) continue;
    if (readUpToId && m.waMessageId === readUpToId) break;
    count++;
  }
  return Math.min(999, count);
}

/** Define o marcador de leitura a partir do contador reportado pela Evolution. */
export function deriveReadCursorFromUnreadCount(
  messages: EvolutionMessage[],
  apiUnread: number
): string | null {
  const inbound = messages
    .filter((m) => !m.isFromMe && m.waMessageId)
    .sort((a, b) => evolutionMessageTimestampMs(a) - evolutionMessageTimestampMs(b));
  if (inbound.length === 0) return null;
  const unread = Math.max(0, Math.floor(apiUnread));
  if (unread <= 0) {
    return inbound[inbound.length - 1]?.waMessageId ?? null;
  }
  const idx = inbound.length - unread - 1;
  if (idx < 0) return null;
  return inbound[idx]?.waMessageId ?? null;
}

/** Rótulo de hora na lista lateral (estilo WhatsApp). */
export function formatChatListTime(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round(
    (todayStart.getTime() - msgStart.getTime()) / 86_400_000
  );
  if (diffDays === 0) {
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }
  if (diffDays === 1) return "Ontem";
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  }
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

/** Converte payload SSE do backend (`/events`) para evento interno do painel. */
export function normalizeEvolutionSsePayload(raw: unknown): EvolutionEvent | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const eventName = String(r.event || r.type || "").toLowerCase();
  const envelope = (r.data as Record<string, unknown>) || r;
  const inner =
    envelope.data && typeof envelope.data === "object"
      ? (envelope.data as Record<string, unknown>)
      : envelope;
  const instance = String(
    r.instance || envelope.instance || inner.instance || ""
  );

  if (
    eventName.includes("messages.upsert") ||
    eventName === "message_upsert" ||
    eventName === "messages_upsert"
  ) {
    const records = extractUpsertMessageRecords(inner);
    for (let i = records.length - 1; i >= 0; i--) {
      registerEvolutionJidsFromMessage(instance, records[i]);
      const mapped = mapEvolutionMessageToOurFormat(records[i]);
      if (mapped) {
        return { type: "MESSAGE_UPSERT", instance, data: { message: mapped } };
      }
    }
    const mapped = mapEvolutionMessageToOurFormat(inner);
    if (!mapped) return null;
    return { type: "MESSAGE_UPSERT", instance, data: { message: mapped } };
  }

  if (
    eventName.includes("chats.upsert") ||
    eventName === "chat_upsert" ||
    eventName === "chats_upsert" ||
    eventName.includes("chats.update") ||
    eventName === "chat_update" ||
    eventName === "chats_update"
  ) {
    const chat = coerceEvolutionChatPayload(inner);
    if (!chat) return null;
    return { type: "CHAT_UPSERT", instance, data: { chat } };
  }

  if (eventName === "sync_ready" || eventName === "ingest_progress") {
    const data = (envelope.data as Record<string, unknown>) || envelope;
    return {
      type: eventName === "ingest_progress" ? "INGEST_PROGRESS" : "SYNC_READY",
      instance,
      data: {
        chatCount: Number(data.chatCount ?? 0),
        messageCount: Number(data.messageCount ?? 0),
        processed: Number(data.processed ?? 0),
        total: Number(data.total ?? 0),
      },
    };
  }

  if (eventName === "messages_hydrated") {
    const data = (envelope.data as Record<string, unknown>) || envelope;
    const remoteJid = String(data.remoteJid ?? "").trim();
    if (!remoteJid) return null;
    return {
      type: "MESSAGES_HYDRATED",
      instance,
      data: {
        remoteJid,
        count: Number(data.count ?? 0),
      },
    };
  }

  if (eventName.includes("messages.update") || eventName.includes("ack")) {
    const key = inner.key as Record<string, unknown> | undefined;
    const waMessageId = String(key?.id || inner.id || "");
    const jid = String(key?.remoteJid || inner.remoteJid || inner.jid || "");
    const ack = String(inner.status ?? inner.ack ?? "");
    if (!waMessageId) return null;
    return {
      type: "ACK_UPDATE",
      instance,
      data: {
        message: {
          jid,
          waMessageId,
          isFromMe: Boolean(key?.fromMe),
          direction: Boolean(key?.fromMe) ? "out" : "in",
          timestamp: "",
          ack: mapEvolutionMessageAck(
            Number(inner.status) || inner.status,
            Boolean(key?.fromMe)
          ),
        },
      },
    };
  }

  return null;
}

function collectAllJidsFromChats(group: EvolutionChat[]): string[] {
  const s = new Set<string>();
  for (const c of group) {
    if (c.jid) s.add(c.jid);
    if (c.mergedRemoteJids) {
      for (const j of c.mergedRemoteJids) {
        if (j) s.add(j);
      }
    }
  }
  return Array.from(s);
}

function pickCanonicalJidForMerged(jids: string[]): string {
  const phoneNonLid = jids.filter(
    (j) => jidLooksLikeWhatsAppPhone(j) && !jidIsPrivacyLid(j)
  );
  if (phoneNonLid.length > 0) {
    phoneNonLid.sort();
    return phoneNonLid[0];
  }
  const lids = jids.filter((j) => jidIsPrivacyLid(j));
  if (lids.length > 0) {
    lids.sort();
    return lids[0];
  }
  return [...jids].sort()[0];
}

function pickPhoneJidForMergedGroup(group: EvolutionChat[], jids: string[]): string | undefined {
  for (const c of group) {
    if (c.phoneJid && jidLooksLikeWhatsAppPhone(c.phoneJid)) return c.phoneJid;
  }
  const canonical = pickCanonicalJidForMerged(jids);
  if (jidLooksLikeWhatsAppPhone(canonical) && !jidIsPrivacyLid(canonical)) {
    return canonical;
  }
  return undefined;
}

function mergeEvolutionChatGroupByPhone(group: EvolutionChat[]): EvolutionChat {
  if (group.length === 0) {
    throw new Error("mergeEvolutionChatGroupByPhone: grupo vazio");
  }
  if (group.length === 1) {
    return group[0];
  }

  const jids = collectAllJidsFromChats(group);
  const canonicalJid = pickCanonicalJidForMerged(jids);
  const mergedRemoteJids = jids.length > 1 ? jids : undefined;
  const phoneJid = pickPhoneJidForMergedGroup(group, jids);
  const bestLabel = pickBestLabelFromChatGroup(group);

  let newest = group[0];
  for (let i = 1; i < group.length; i++) {
    const c = group[i];
    if (evolutionChatLastActivityMs(c) >= evolutionChatLastActivityMs(newest)) {
      newest = c;
    }
  }

  const older = group.find((c) => c !== newest) ?? newest;
  const unreadCount = Math.min(
    999,
    group.reduce((max, c) => Math.max(max, c.unreadCount || 0), 0)
  );

  return {
    jid: canonicalJid,
    mergedRemoteJids,
    phoneJid,
    pushName: bestLabel ?? newest.pushName ?? older.pushName,
    name: bestLabel ?? newest.name ?? older.name,
    title: newest.title ?? older.title,
    profilePictureUrl: newest.profilePictureUrl ?? older.profilePictureUrl,
    unreadCount,
    lastMessage: newest.lastMessage || older.lastMessage,
    lastMessageTime: newest.lastMessageTime || older.lastMessageTime,
    archived: group.some((ch) => Boolean(ch.archived)),
    lastMessageKey: newest.lastMessageKey ?? older.lastMessageKey,
    isGroup: Boolean(newest.isGroup),
    participants: newest.participants ?? older.participants,
    botPaused: group.some((ch) => ch.botPaused === true),
    botAttention: group.some((ch) => ch.botAttention === "urgent")
      ? "urgent"
      : group.some((ch) => ch.botAttention === "watch")
        ? "watch"
        : newest.botAttention ?? older.botAttention,
  };
}

/**
 * Chave do mapa na 1ª deduplicação: mesmo número (`p:`) funde já aqui (ex.: `@c.us` vs `@s.whatsapp.net`).
 */
function evolutionChatPrimaryDedupeMapKey(c: EvolutionChat): string | null {
  if (!c.jid) return null;
  const j = c.jid.toLowerCase();
  if (c.isGroup || j.endsWith("@g.us")) return `g:${c.jid}`;
  const pk = phoneDedupeKeyForChat(c);
  if (pk) return `p:${pk}`;
  const lidKey = resolvePrivacyLidPhoneKey(c);
  if (lidKey) return `p:${lidKey}`;
  return `j:${c.jid}`;
}

function dedupeEvolutionChatsByJid(chats: EvolutionChat[]): EvolutionChat[] {
  const map = new Map<string, EvolutionChat>();
  for (const c of chats) {
    const mapKey = evolutionChatPrimaryDedupeMapKey(c);
    if (!mapKey) continue;
    const prev = map.get(mapKey);
    if (!prev) {
      map.set(mapKey, c);
      continue;
    }
    if (mapKey.startsWith("p:")) {
      map.set(mapKey, mergeEvolutionChatGroupByPhone([prev, c]));
    } else {
      const labels = mergeChatLabelsForDuplicateJid(prev, c);
      map.set(mapKey, {
        ...prev,
        ...c,
        unreadCount: Math.max(prev.unreadCount, c.unreadCount),
        pushName: labels.pushName ?? c.pushName ?? prev.pushName,
        name: labels.name ?? c.name ?? prev.name,
        title: labels.title ?? c.title ?? prev.title,
        lastMessage: c.lastMessage || prev.lastMessage,
        lastMessageTime: c.lastMessageTime || prev.lastMessageTime,
        phoneJid: c.phoneJid || prev.phoneJid,
        archived: mergeArchivedChatFlag(prev.archived, c.archived),
        lastMessageKey: c.lastMessageKey ?? prev.lastMessageKey,
      });
    }
  }
  return Array.from(map.values());
}

/**
 * Une conversas 1:1 que representam o mesmo telefone (ex.: entrada por @lid e saída por @s.whatsapp.net).
 * Chats @lid sem telefone explícito são fundidos quando lastMessage traz remoteJidAlt do mesmo número.
 */
function dedupeEvolutionChatsByPhone(chats: EvolutionChat[]): EvolutionChat[] {
  const groups: EvolutionChat[] = [];
  const singlesNoKey: EvolutionChat[] = [];
  const byPhone = new Map<string, EvolutionChat[]>();

  for (const c of chats) {
    if (c.isGroup || (c.jid || "").toLowerCase().endsWith("@g.us")) {
      groups.push(c);
      continue;
    }
    const key =
      phoneDedupeKeyForChat(c) ?? resolvePrivacyLidPhoneKey(c);
    if (!key) {
      singlesNoKey.push(c);
      continue;
    }
    const arr = byPhone.get(key) || [];
    arr.push(c);
    byPhone.set(key, arr);
  }

  const out: EvolutionChat[] = [];

  for (const [, arr] of byPhone) {
    out.push(
      arr.length === 1 ? arr[0] : mergeEvolutionChatGroupByPhone(arr)
    );
  }

  const mergedPhoneKeys = new Set<string>();
  for (const c of out) {
    const k = phoneDedupeKeyForChat(c);
    if (k) mergedPhoneKeys.add(k);
  }

  for (const c of singlesNoKey) {
    if (jidIsPrivacyLid(c.jid || "")) {
      const lidKey = resolvePrivacyLidPhoneKey(c);
      if (lidKey && mergedPhoneKeys.has(lidKey)) {
        continue;
      }
    }
    out.push(c);
  }

  out.push(...groups);
  return out;
}

/**
 * Remove linhas @lid órfãs quando já existe conversa com o mesmo telefone na lista.
 */
function collapseRedundantPrivacyLidChats(chats: EvolutionChat[]): EvolutionChat[] {
  const phoneKeyed = new Map<string, EvolutionChat>();
  for (const c of chats) {
    const k = phoneDedupeKeyForChat(c);
    if (!k) continue;
    const prev = phoneKeyed.get(k);
    if (!prev || evolutionChatLastActivityMs(c) >= evolutionChatLastActivityMs(prev)) {
      phoneKeyed.set(k, c);
    }
  }

  const absorb = new Map<string, EvolutionChat[]>();
  const keep: EvolutionChat[] = [];

  for (const c of chats) {
    if (!jidIsPrivacyLid(c.jid || "")) {
      keep.push(c);
      continue;
    }
    const k = phoneDedupeKeyForChat(c) ?? resolvePrivacyLidPhoneKey(c);
    if (k && phoneKeyed.has(k) && phoneKeyed.get(k) !== c) {
      const arr = absorb.get(k) || [];
      arr.push(c);
      absorb.set(k, arr);
      continue;
    }
    if (!k) {
      keep.push(c);
      continue;
    }
    keep.push(c);
  }

  return keep.map((c) => {
    const k = phoneDedupeKeyForChat(c);
    if (!k) return c;
    const extras = absorb.get(k);
    if (!extras?.length) return c;
    return mergeEvolutionChatGroupByPhone([c, ...extras]);
  });
}

/**
 * Se a última mensagem do chat é nossa (`lastMessageKey.fromMe`), não há "não lidas" do contacto.
 * A Evolution/WhatsApp por vezes mantém `unreadCount > 0` nesse caso — corrige o badge na lista.
 */
export function zeroUnreadIfLastMessageFromMe(c: EvolutionChat): EvolutionChat {
  const n = Number(c.unreadCount) || 0;
  if (n <= 0) return c;
  if (c.lastMessageKey?.fromMe === true) {
    return { ...c, unreadCount: 0 };
  }
  return c;
}

/** Deduplica por JID e depois por número (lista vinda da API ou após eventos em tempo real). */
export function dedupeAndMergeEvolutionChats(chats: EvolutionChat[]): EvolutionChat[] {
  const merged = collapseRedundantPrivacyLidChats(
    dedupeEvolutionChatsByPhone(dedupeEvolutionChatsByJid(chats))
  );
  return merged.map(zeroUnreadIfLastMessageFromMe);
}

/** JIDs a consultar no `findMessages` para montar o fio único (cronológico após merge). */
export function getFetchJidsForEvolutionChat(chat: EvolutionChat): string[] {
  const set = new Set<string>();
  if (chat.jid) set.add(chat.jid);
  if (chat.phoneJid && chat.phoneJid !== chat.jid) set.add(chat.phoneJid);
  if (chat.mergedRemoteJids) {
    for (const j of chat.mergedRemoteJids) {
      if (j) set.add(j);
    }
  }
  return [...set];
}

/** Lê `archived` / variantes da Evolution ou Baileys sem tratar a string `"false"` como true. */
function parseEvolutionChatArchived(raw: Record<string, unknown>): boolean {
  const tryVal = (v: unknown): boolean | null => {
    if (v === true || v === 1) return true;
    if (v === false || v === 0) return false;
    if (typeof v === "string") {
      const s = v.toLowerCase().trim();
      if (s === "true" || s === "1" || s === "yes") return true;
      if (s === "false" || s === "0" || s === "no" || s === "") return false;
    }
    return null;
  };
  const top = tryVal(raw.archived ?? raw.isArchived ?? raw.archive);
  if (top !== null) return top;

  const chatObj = raw.chat;
  if (chatObj && typeof chatObj === "object") {
    const c = chatObj as Record<string, unknown>;
    const nested = tryVal(c.archived ?? c.isArchived ?? c.archive);
    if (nested !== null) return nested;
  }
  const conv = raw.conversation;
  if (conv && typeof conv === "object") {
    const nested = tryVal((conv as Record<string, unknown>).archived);
    if (nested !== null) return nested;
  }

  return Boolean(raw.archived ?? raw.isArchived ?? raw.archive);
}

/**
 * JIDs marcados como arquivados neste browser após `archiveChat` com sucesso.
 * A Evolution às vezes não devolve `archived: true` no findChats — assim o refresh mantém a lista coerente.
 */
const USER_ARCHIVED_STORAGE_KEY = "delivai.evolution.userArchivedJids.v1";

export function persistUserArchivedJidsForInstance(
  instance: string,
  jids: Iterable<string>,
  archived: boolean
): void {
  try {
    const inst = String(instance || "").trim();
    if (!inst) return;
    const raw = sessionStorage.getItem(USER_ARCHIVED_STORAGE_KEY);
    const all = (raw ? JSON.parse(raw) : {}) as Record<string, string[]>;
    const set = new Set(all[inst] ?? []);
    for (const j of jids) {
      const id = String(j || "").trim();
      if (!id) continue;
      if (archived) set.add(id);
      else set.delete(id);
    }
    all[inst] = Array.from(set);
    sessionStorage.setItem(USER_ARCHIVED_STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* quota / modo privado */
  }
}

export function applyUserArchivedJidsFromStorage(
  instance: string,
  chats: EvolutionChat[]
): EvolutionChat[] {
  try {
    const inst = String(instance || "").trim();
    if (!inst) return chats;
    const raw = sessionStorage.getItem(USER_ARCHIVED_STORAGE_KEY);
    const all = (raw ? JSON.parse(raw) : {}) as Record<string, string[]>;
    const stored = new Set(
      (all[inst] ?? []).map((j) => String(j).trim()).filter(Boolean)
    );
    if (stored.size === 0) return chats;

    const base = dedupeAndMergeEvolutionChats(chats);

    const flagged = base.map((c) => {
      const ids = [c.jid, ...(c.mergedRemoteJids ?? [])].filter(Boolean) as string[];
      if (ids.some((id) => stored.has(id))) {
        return { ...c, archived: true };
      }
      return c;
    });

    const present = new Set<string>();
    for (const c of flagged) {
      for (const id of [c.jid, ...(c.mergedRemoteJids ?? [])]) {
        if (id) present.add(id);
      }
    }

    /** findChats costuma omitir arquivadas — reintroduz entrada mínima para cada JID ainda no storage. */
    const extras: EvolutionChat[] = [];
    for (const jid of stored) {
      if (present.has(jid)) continue;
      extras.push({
        jid,
        unreadCount: 0,
        isGroup: jid.toLowerCase().endsWith("@g.us"),
        archived: true,
        lastMessage: "Arquivada",
      });
    }

    return dedupeAndMergeEvolutionChats([...flagged, ...extras]);
  } catch {
    return chats;
  }
}

/** Converte um item bruto da Evolution (lista ou webhook) em chat com `jid` confiável. */
export function coerceEvolutionChatPayload(item: unknown): EvolutionChat | null {
  if (!item || typeof item !== "object") return null;
  const c = item as Record<string, unknown>;
  const jid = extractChatRemoteJid(c);
  if (!jid) return null;
  const jLower = jid.toLowerCase();
  if (jLower === "status@broadcast") return null;
  const t = (v: unknown): string =>
    v != null && String(v).trim() ? String(v).trim() : "";
  const pn =
    typeof c.participantPn === "string" ? c.participantPn.trim() : "";
  const altFromLast = extractRemoteJidAltFromLastMessage(c.lastMessage);
  const phoneFromLastKey = extractPhoneJidFromLastMessageKey(c.lastMessage);
  const alt =
    t(c.remoteJidAlt) ||
    t(c.jidAlternative) ||
    pn ||
    altFromLast ||
    "";
  let phoneJid: string | undefined;
  if (alt && /@s\.whatsapp\.net$/i.test(alt)) {
    phoneJid = alt;
  } else if (alt && /@c\.us$/i.test(alt)) {
    phoneJid = alt.replace(/@c\.us$/i, "@s.whatsapp.net");
  } else if (alt && !alt.includes("@") && /^\d{10,15}$/.test(alt.replace(/\D/g, ""))) {
    phoneJid = `${alt.replace(/\D/g, "")}@s.whatsapp.net`;
  } else {
    const norm = alt ? normalizeCoercePhoneJid(alt) : undefined;
    if (norm) phoneJid = norm;
  }
  if (!phoneJid && phoneFromLastKey) {
    phoneJid = phoneFromLastKey;
  }
  let displayName =
    pickChatDisplayName(c) || extractPushNameFromLastMessage(c.lastMessage);
  if (displayName && looksLikePhoneOnlyLabel(displayName)) {
    const fromLast = extractPushNameFromLastMessage(c.lastMessage);
    if (fromLast && !looksLikePhoneOnlyLabel(fromLast)) {
      displayName = fromLast;
    }
  }
  const lastMessageStr =
    extractLastMessagePreview(c.lastMessage) ??
    (typeof c.lastMessage === "string" ? c.lastMessage.trim() || undefined : undefined);
  const pic =
    c.profilePictureUrl ?? c.profilePicUrl ?? (c as Record<string, unknown>).profile_pic_url;
  return {
    jid,
    phoneJid,
    title: c.title != null ? String(c.title) : undefined,
    // Mantém o melhor rótulo já resolvido (agenda > pushName) nos dois campos,
    // para não perder o nome salvo quando payloads parciais chegam em updates.
    pushName: displayName ?? (c.pushName != null ? String(c.pushName) : undefined),
    name:
      displayName ??
      (c.name != null ? String(c.name) : undefined) ??
      (c.pushName != null ? String(c.pushName) : undefined),
    profilePictureUrl: pic != null ? String(pic) : undefined,
    unreadCount: parseEvolutionUnreadCount(c),
    lastMessage: lastMessageStr,
    lastMessageTime: c.lastMessageTime as string | undefined,
    archived: parseEvolutionChatArchived(c),
    lastMessageKey: extractLastMessageKeyFromRaw(c.lastMessage, jid),
    isGroup: Boolean(c.isGroup),
    participants: Array.isArray(c.participants) ? (c.participants as string[]) : undefined,
    botPaused: parseBotPausedFlag(c),
    botAttention: parseBotAttentionFlag(c),
  } as EvolutionChat;
}

// Normaliza resposta da Evolution API (chats podem vir em data, chats ou array direto)
function normalizeEvolutionChats(raw: unknown): EvolutionChat[] {
  let arr: unknown[] = [];
  if (Array.isArray(raw)) arr = raw;
  else if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    const a = obj.chats ?? obj.contacts ?? obj.data ?? obj.records ?? obj;
    if (Array.isArray(a)) arr = a;
  }
  const mapped: EvolutionChat[] = [];
  for (const item of arr) {
    const chat = coerceEvolutionChatPayload(item);
    if (chat) mapped.push(chat);
  }
  return dedupeEvolutionChatsByPhone(dedupeEvolutionChatsByJid(mapped));
}

/** Mapeia telefone → @lid (mensagens Baileys recentes) quando findChats está indisponível. */
const evolutionPhoneToLidCache = new Map<string, string>();
const evolutionLidToPhoneCache = new Map<string, string>();

function phoneLidCacheKey(instance: string, phoneJid: string): string {
  return `${instance}:${phoneJid}`;
}

function lidPhoneCacheKey(instance: string, lidJid: string): string {
  return `${instance}:${lidJid.toLowerCase()}`;
}

function registerPhoneLidMapping(
  instance: string,
  phoneJid: string | undefined,
  lidJid: string | undefined
): void {
  if (!phoneJid || !lidJid || !lidJid.toLowerCase().endsWith("@lid")) return;
  evolutionPhoneToLidCache.set(phoneLidCacheKey(instance, phoneJid), lidJid);
  evolutionLidToPhoneCache.set(lidPhoneCacheKey(instance, lidJid), phoneJid);
}

function lookupLidForPhone(instance: string, phoneJid: string): string | undefined {
  return evolutionPhoneToLidCache.get(phoneLidCacheKey(instance, phoneJid));
}

function lookupPhoneForLid(instance: string, lidJid: string): string | undefined {
  return evolutionLidToPhoneCache.get(lidPhoneCacheKey(instance, lidJid));
}

/** Expande JIDs do mesmo contato (@lid ↔ telefone) para patch de lista/SSE. */
export function expandEvolutionContactJids(instance: string, jid: string): string[] {
  const out = new Set<string>([jid]);
  const phone = lookupPhoneForLid(instance, jid);
  const lid = lookupLidForPhone(instance, jid);
  if (phone) out.add(phone);
  if (lid) out.add(lid);
  return [...out];
}

/** Registra mapeamento phone↔lid a partir de key Baileys (webhook/SSE). */
export function registerEvolutionJidsFromMessage(
  instance: string,
  item: Record<string, unknown>
): void {
  const key =
    item.key && typeof item.key === "object"
      ? (item.key as Record<string, unknown>)
      : undefined;
  const remoteJid = String(key?.remoteJid ?? item.remoteJid ?? "").trim();
  const alt = String(key?.remoteJidAlt ?? item.remoteJidAlt ?? "").trim();
  if (!remoteJid || !alt) return;
  const rLower = remoteJid.toLowerCase();
  const aLower = alt.toLowerCase();
  if (rLower.endsWith("@s.whatsapp.net") && aLower.endsWith("@lid")) {
    registerPhoneLidMapping(instance, remoteJid, alt);
  } else if (rLower.endsWith("@lid") && aLower.endsWith("@s.whatsapp.net")) {
    registerPhoneLidMapping(instance, alt, remoteJid);
  }
}

function extractUpsertMessageRecords(inner: Record<string, unknown>): Record<string, unknown>[] {
  if (Array.isArray(inner)) {
    return inner.filter((x): x is Record<string, unknown> => !!x && typeof x === "object");
  }
  const nested = inner.messages;
  if (Array.isArray(nested)) {
    return nested.filter((x): x is Record<string, unknown> => !!x && typeof x === "object");
  }
  if (inner.key || inner.message) return [inner];
  return [];
}

function normalizeAltPhoneJid(alt: string): string | undefined {
  const trimmed = alt.trim();
  if (!trimmed) return undefined;
  const withoutDevice = trimmed.replace(/:\d+@/, "@");
  return (
    normalizeCoercePhoneJid(withoutDevice) ?? normalizeCoercePhoneJid(trimmed)
  );
}

function phoneJidFromMessageKey(key: Record<string, unknown>): string | undefined {
  const alt =
    typeof key.remoteJidAlt === "string" ? key.remoteJidAlt.trim() : "";
  if (alt) {
    const fromAlt = normalizeAltPhoneJid(alt);
    if (fromAlt) return fromAlt;
  }
  const rj = typeof key.remoteJid === "string" ? key.remoteJid.trim() : "";
  if (rj && jidLooksLikeWhatsAppPhone(rj) && !jidIsPrivacyLid(rj)) {
    return normalizeCoercePhoneJid(rj);
  }
  return undefined;
}

async function fetchEvolutionMessageRecordsDirect(
  instanceName: string,
  base: string,
  headers: Record<string, string>,
  opts?: {
    remoteJid?: string;
    remoteJidAlt?: string;
    offset?: number;
    page?: number;
  }
): Promise<unknown[]> {
  const limit = opts?.offset ?? 800;
  const page = opts?.page ?? 1;
  const where: Record<string, unknown> = {};
  if (opts?.remoteJid) {
    where.key = { remoteJid: opts.remoteJid };
  } else if (opts?.remoteJidAlt) {
    where.key = { remoteJidAlt: opts.remoteJidAlt };
  }
  const body = JSON.stringify({
    ...(Object.keys(where).length > 0 ? { where } : {}),
    page,
    offset: limit,
  });
  const path = `${EVOLUTION_CONFIG.ENDPOINTS.FIND_MESSAGES}/${encodeURIComponent(instanceName)}`;
  const res = await fetch(`${base}${path}`, { method: "POST", headers, body });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return [];
  return extractMessagesArray(data);
}

async function fetchRecentEvolutionMessageRecords(
  instanceName: string,
  base: string,
  headers: Record<string, string>
): Promise<unknown[]> {
  const pageSize = 500;
  const pages = 4;
  const merged: unknown[] = [];
  const seen = new Set<string>();

  for (let page = 1; page <= pages; page++) {
    const batch = await fetchEvolutionMessageRecordsDirect(
      instanceName,
      base,
      headers,
      { offset: pageSize, page }
    );
    if (batch.length === 0) break;
    for (const item of batch) {
      if (!item || typeof item !== "object") continue;
      const key = (item as Record<string, unknown>).key;
      const id =
        key && typeof key === "object"
          ? String((key as Record<string, unknown>).id ?? "")
          : "";
      const dedupeKey = id || JSON.stringify(item).slice(0, 120);
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      merged.push(item);
    }
    if (batch.length < pageSize) break;
  }

  merged.sort((a, b) => {
    const ma = mapEvolutionMessageToOurFormat(a);
    const mb = mapEvolutionMessageToOurFormat(b);
    const ta = ma ? evolutionMessageTimestampMs(ma) : 0;
    const tb = mb ? evolutionMessageTimestampMs(mb) : 0;
    return tb - ta;
  });

  return merged;
}

function indexContactsByPhone(contacts: EvolutionChat[]): Map<string, EvolutionChat> {
  const map = new Map<string, EvolutionChat>();
  for (const c of contacts) {
    const keys = new Set<string>();
    if (c.phoneJid) keys.add(c.phoneJid.toLowerCase());
    if (c.jid && jidLooksLikeWhatsAppPhone(c.jid)) keys.add(c.jid.toLowerCase());
    for (const k of keys) map.set(k, c);
  }
  return map;
}

/** Monta conversas reais (com histórico) a partir de findMessages — paridade WhatsApp Web. */
function buildChatsFromMessageRecords(
  instanceName: string,
  records: unknown[],
  contactsByPhone?: Map<string, EvolutionChat>
): EvolutionChat[] {
  for (const item of records) {
    if (!item || typeof item !== "object") continue;
    const key = (item as Record<string, unknown>).key;
    if (!key || typeof key !== "object") continue;
    const k = key as Record<string, unknown>;
    const remoteJid = typeof k.remoteJid === "string" ? k.remoteJid.trim() : "";
    const phoneJid = phoneJidFromMessageKey(k);
    const lidJid = remoteJid.toLowerCase().endsWith("@lid") ? remoteJid : undefined;
    if (phoneJid && lidJid) registerPhoneLidMapping(instanceName, phoneJid, lidJid);
  }

  const byChat = new Map<
    string,
    { chat: EvolutionChat; lastMs: number }
  >();

  for (const item of records) {
    if (!item || typeof item !== "object") continue;
    const raw = item as Record<string, unknown>;
    const key = raw.key;
    if (!key || typeof key !== "object") continue;
    const k = key as Record<string, unknown>;
    const remoteJid = typeof k.remoteJid === "string" ? k.remoteJid.trim() : "";
    if (!remoteJid || remoteJid === "status@broadcast") continue;

    const phoneJid = phoneJidFromMessageKey(k);
    const lidJid = remoteJid.toLowerCase().endsWith("@lid") ? remoteJid : undefined;
    const phoneFromLid =
      !phoneJid && lidJid ? lookupPhoneForLid(instanceName, lidJid) : undefined;
    const effectivePhone = phoneJid ?? phoneFromLid;

    const primaryJid = effectivePhone ?? remoteJid;
    const mapped = mapEvolutionMessageToOurFormat(item);
    if (!mapped) continue;
    const lastMs = evolutionMessageTimestampMs(mapped);
    const mapKey = primaryJid.toLowerCase();

    const mergedRemoteJids = new Set<string>();
    if (lidJid && lidJid !== primaryJid) mergedRemoteJids.add(lidJid);
    if (remoteJid && remoteJid !== primaryJid) mergedRemoteJids.add(remoteJid);
    if (effectivePhone && effectivePhone !== primaryJid) {
      mergedRemoteJids.add(effectivePhone);
    }

    const contact = effectivePhone
      ? contactsByPhone?.get(effectivePhone.toLowerCase())
      : contactsByPhone?.get(primaryJid.toLowerCase());

    const msgPushName =
      extractPushNameFromLastMessage(item) ||
      (typeof raw.pushName === "string" ? raw.pushName.trim() : "") ||
      pickChatDisplayName(raw) ||
      "";

    const lastMessageKey = extractLastMessageKeyFromRaw(item, primaryJid);

    const prev = byChat.get(mapKey);
    if (prev && prev.lastMs >= lastMs) continue;

    byChat.set(mapKey, {
      lastMs,
      chat: {
        jid: primaryJid,
        phoneJid: effectivePhone,
        mergedRemoteJids:
          mergedRemoteJids.size > 0 ? [...mergedRemoteJids] : undefined,
        pushName:
          contact?.pushName ??
          contact?.name ??
          (msgPushName || undefined),
        name:
          contact?.name ??
          contact?.pushName ??
          (msgPushName || undefined),
        profilePictureUrl: contact?.profilePictureUrl,
        unreadCount: 0,
        lastMessage: sidebarPreviewFromEvolutionMessage(mapped),
        lastMessageTime: mapped.timestamp,
        lastMessageKey,
        isGroup: primaryJid.toLowerCase().endsWith("@g.us"),
      },
    });
  }

  return [...byChat.values()]
    .sort((a, b) => b.lastMs - a.lastMs)
    .map((x) => x.chat);
}

async function buildChatsFromRecentMessages(
  instanceName: string,
  base: string,
  headers: Record<string, string>,
  contacts?: EvolutionChat[]
): Promise<EvolutionChat[]> {
  const records = await fetchRecentEvolutionMessageRecords(
    instanceName,
    base,
    headers
  );
  if (records.length === 0) return [];
  const contactsByPhone = contacts?.length
    ? indexContactsByPhone(contacts)
    : undefined;
  return dedupeAndMergeEvolutionChats(
    enrichChatsWithContactNames(
      buildChatsFromMessageRecords(instanceName, records, contactsByPhone),
      contacts
    )
  );
}

function enrichChatsWithContactNames(
  chats: EvolutionChat[],
  contacts?: EvolutionChat[]
): EvolutionChat[] {
  if (!contacts?.length) return chats;
  const byPhone = indexContactsByPhone(contacts);
  return chats.map((c) => {
    const keys = new Set<string>();
    if (c.jid && jidLooksLikeWhatsAppPhone(c.jid)) keys.add(c.jid.toLowerCase());
    if (c.phoneJid) keys.add(c.phoneJid.toLowerCase());
    let contact: EvolutionChat | undefined;
    for (const j of keys) {
      contact = byPhone.get(j);
      if (contact) break;
    }
    if (!contact) return c;
    const savedName = contact.name || contact.title;
    const publicName = contact.pushName;
    return {
      ...c,
      name: c.name || savedName || publicName,
      pushName: c.pushName || publicName || savedName,
      profilePictureUrl: c.profilePictureUrl || contact.profilePictureUrl,
    };
  });
}

// Lista conversas direto na Evolution API (dev: proxy /api/evolution; prod: URL pública).
async function fetchConversationsFromEvolutionDirect(
  instanceName: string
): Promise<EvolutionChat[]> {
  if (!evolutionDirectReady()) return [];
  const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
  const headers = getAuthHeaders();

  const tryEndpoint = async (
    path: string,
    method: "GET" | "POST" = "POST",
    body?: string
  ): Promise<EvolutionChat[] | null> => {
    const url = `${base}${path}/${encodeURIComponent(instanceName)}`;
    const res = await fetch(url, {
      method,
      headers,
      ...(method === "POST" ? { body: body ?? "{}" } : {}),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) return normalizeEvolutionChats(data);
    if (path.includes("findChats")) {
      console.warn(
        "[DelivAI] Evolution findChats HTTP",
        res.status,
        parseEvolutionApiErrorResponse(data, res.status)
      );
    }
    return null;
  };

  let findChatsBroken = false;
  let list = await tryEndpoint(EVOLUTION_CONFIG.ENDPOINTS.FIND_CHATS, "POST", "{}");
  if (!list) {
    findChatsBroken = true;
    list = [];
  }

  if (list.length === 0) {
    const fromContactsRaw =
      (await tryEndpoint(
        EVOLUTION_CONFIG.ENDPOINTS.FIND_CONTACTS,
        "POST",
        "{}"
      )) ?? [];
    const fromContacts = fromContactsRaw.filter(
      (c) => c.jid && !c.jid.includes("status@broadcast")
    );

    const fromMessages = await buildChatsFromRecentMessages(
      instanceName,
      base,
      headers,
      fromContacts
    );

    if (fromMessages.length > 0) {
      console.warn(
        "[DelivAI] findChats indisponível — lista via findMessages (",
        fromMessages.length,
        "conversas com histórico). Contatos da agenda:",
        fromContacts.length
      );
      list = fromMessages;
    } else if (fromContacts.length > 0) {
      console.warn(
        "[DelivAI] findChats/findMessages vazios — fallback findContacts (",
        fromContacts.length,
        "contatos, sem histórico garantido)."
      );
      list = fromContacts;
    }
  }

  const allFromFindAll =
    (await tryEndpoint(EVOLUTION_CONFIG.ENDPOINTS.CONVERSATIONS, "GET")) ?? [];
  if (allFromFindAll.length > 0) {
    list = [...list, ...allFromFindAll];
  }

  if (findChatsBroken && list.length > 0) {
    return applyUserArchivedJidsFromStorage(instanceName, list);
  }

  const archivedBodies = [
    '{"where":{"archived":true}}',
    '{"where":{"archived":true},"limit":500}',
    '{"where":{"archived":true},"page":1,"offset":500}',
    '{"where":{"archive":true}}',
    '{"where":{"isArchived":true}}',
    '{"where":{"chat":{"archived":true}}}',
    '{"where":{"conversation":{"archived":true}}}',
    '{"archived":true}',
    '{"archive":true}',
    '{"isArchived":true}',
  ];
  const chatIds = (c: EvolutionChat): string[] =>
    [c.jid, ...(c.mergedRemoteJids ?? [])].filter(Boolean) as string[];
  const mainIdSet = new Set<string>();
  for (const c of list) {
    for (const id of chatIds(c)) mainIdSet.add(id);
  }
  const archivedIdSet = new Set<string>();
  const extrasToAppend: EvolutionChat[] = [];

  for (const c of list) {
    if (!c.archived) continue;
    for (const id of chatIds(c)) archivedIdSet.add(id);
  }

  for (const body of archivedBodies) {
    const extra = await tryEndpoint(
      EVOLUTION_CONFIG.ENDPOINTS.FIND_CHATS,
      "POST",
      body
    );
    if (!extra || extra.length === 0) continue;

    let overlapWithMain = 0;
    let explicitArchived = 0;
    for (const c of extra) {
      const ids = chatIds(c);
      if (ids.some((id) => mainIdSet.has(id))) overlapWithMain++;
      if (c.archived) explicitArchived++;
    }
    const overlapRatio = overlapWithMain / extra.length;
    const looksUnfiltered =
      explicitArchived === 0 &&
      overlapRatio > 0.9 &&
      extra.length >= Math.max(10, Math.floor(list.length * 0.9));
    if (looksUnfiltered) continue;

    for (const c of extra) {
      for (const id of chatIds(c)) archivedIdSet.add(id);
      const ids = chatIds(c);
      const alreadyInMain = ids.some((id) => mainIdSet.has(id));
      if (!alreadyInMain) extrasToAppend.push({ ...c, archived: true });
    }
    break;
  }

  const flaggedMain = list.map((c) => {
    const archived =
      c.archived === true || chatIds(c).some((id) => archivedIdSet.has(id));
    return archived === c.archived ? c : { ...c, archived };
  });

  return applyUserArchivedJidsFromStorage(instanceName, [
    ...flaggedMain,
    ...extrasToAppend,
  ]);
}

/** Une backend operacional + Evolution direto — paridade WhatsApp Web (inclui arquivadas). */
function mergeConversationSources(
  instanceName: string,
  backendRecords: EvolutionChat[],
  directRecords: EvolutionChat[],
  operationalDb: boolean
): EvolutionChat[] {
  if (backendRecords.length === 0) return directRecords;
  if (directRecords.length === 0) {
    return applyUserArchivedJidsFromStorage(instanceName, backendRecords);
  }
  const merged = dedupeAndMergeEvolutionChats([
    ...directRecords,
    ...backendRecords,
  ]);
  return applyUserArchivedJidsFromStorage(instanceName, merged);
}

export type FetchConversationsResult = {
  chats: EvolutionChat[];
  backendError?: string;
  /** Postgres operacional vazio — sync/warmup em andamento no backend. */
  awaitingBackendSync?: boolean;
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchBackendConversationRecords(
  instanceName: string
): Promise<{
  records: EvolutionChat[];
  operationalDb: boolean;
  error?: string;
}> {
  try {
    const data = await evolutionBackendRequest("GET", "/api/evolution/chats", {
      params: { instance: instanceName },
      timeoutMs: EVOLUTION_BACKEND_CHATS_TIMEOUT_MS,
    });
    const payload =
      data && typeof data === "object" ? (data as Record<string, unknown>) : {};
    return {
      records: normalizeEvolutionChats(data),
      operationalDb: payload.source === "operational_db",
    };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn("[DelivAI] backend /api/evolution/chats:", msg);
    return { records: [], operationalDb: false, error: msg };
  }
}

/**
 * Lista conversas: backend Kotlin primeiro (como produção), Evolution direto só enriquece/fallback.
 */
async function fetchConversationsFromEvolution(
  instanceName: string
): Promise<FetchConversationsResult> {
  if (!shouldUseEvolutionBackendProxy()) {
    const useDirect = shouldUseEvolutionDirectFallback();
    let direct: EvolutionChat[] = [];
    if (useDirect) {
      try {
        direct = await fetchConversationsFromEvolutionDirect(instanceName);
      } catch (e) {
        console.warn("[DelivAI] Evolution findChats:", e);
      }
    }
    return {
      chats: applyUserArchivedJidsFromStorage(instanceName, direct),
    };
  }

  await requestEvolutionWarmup(instanceName);

  let { records: backendRecords, operationalDb, error: backendError } =
    await fetchBackendConversationRecords(instanceName);

  if (
    backendRecords.length === 0 &&
    operationalDb &&
    !backendError
  ) {
    await sleep(2_500);
    const retry = await fetchBackendConversationRecords(instanceName);
    backendRecords = retry.records;
    operationalDb = retry.operationalDb;
    backendError = retry.error;
  }

  let direct: EvolutionChat[] = [];
  const needsDirectFallback =
    shouldUseEvolutionDirectFallback() &&
    (backendRecords.length === 0 || Boolean(backendError));
  if (needsDirectFallback) {
    try {
      direct = await fetchConversationsFromEvolutionDirect(instanceName);
    } catch (e) {
      console.warn("[DelivAI] Evolution findChats (fallback):", e);
    }
  }

  if (direct.length > 0 || backendRecords.length > 0) {
    return {
      chats: mergeConversationSources(
        instanceName,
        backendRecords,
        direct,
        operationalDb
      ),
    };
  }

  if (backendError && direct.length === 0) {
    return { chats: [], backendError };
  }

  if (operationalDb && direct.length === 0) {
    return { chats: [], awaitingBackendSync: true };
  }

  return { chats: [] };
}

// Conversas via backend Kotlin (/api/evolution/chats) ou fallback Evolution API direta.
export async function fetchConversationsFromN8n(
  instance: string
): Promise<EvolutionChat[]> {
  return (await fetchConversationsFromEvolution(instance)).chats;
}

export async function fetchConversationsWithMeta(
  instance: string
): Promise<FetchConversationsResult> {
  return fetchConversationsFromEvolution(instance);
}

/** Dispara warmup em background no backend (após QR / primeira conexão). */
export async function requestEvolutionWarmup(instance: string): Promise<void> {
  if (!shouldUseEvolutionBackendProxy()) return;
  const inst = instance.replace(/\D/g, "");
  if (!inst) return;
  try {
    await evolutionBackendRequest("POST", "/api/evolution/warmup", {
      params: { instance: inst },
    });
  } catch (e) {
    console.warn("[DelivAI] evolution warmup:", e);
  }
}

// Cache de foto de perfil (sucesso ou falha) para não repetir requisições que retornam 500
const profilePictureCache = new Map<string, string | null>();

// Evolution costuma retornar 500 em fetchProfilePictureUrl; desativado por padrão. Use REACT_APP_DISABLE_PROFILE_PICTURE=false para ativar.
const DISABLE_PROFILE_PICTURE = process.env.REACT_APP_DISABLE_PROFILE_PICTURE !== "false";

/**
 * Obtém a URL da foto de perfil de um contato (Evolution API).
 * Chamado para cada conversa na lista (por isso várias requisições a fetchProfilePictureUrl/{instance}).
 * A Evolution pode retornar 500 nesse endpoint; use REACT_APP_DISABLE_PROFILE_PICTURE=true para desativar e evitar erros no console.
 */
export async function getEvolutionProfilePictureUrl(
  instanceName: string,
  jid: string
): Promise<string | null> {
  if (DISABLE_PROFILE_PICTURE || !jid) return null;

  const cacheKey = `${instanceName}:${jid}`;
  if (profilePictureCache.has(cacheKey)) return profilePictureCache.get(cacheKey)!;

  if (shouldUseEvolutionBackendProxy()) {
    try {
      const data = (await evolutionBackendRequest("POST", "/api/evolution/profile-picture", {
        body: { instance: instanceName, jid },
      })) as { ok?: boolean; profilePictureUrl?: string | null };
      const url = data.profilePictureUrl ?? null;
      profilePictureCache.set(cacheKey, url);
      return url;
    } catch {
      if (!shouldUseEvolutionDirectFallback()) {
        profilePictureCache.set(cacheKey, null);
        return null;
      }
    }
  }

  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) return null;

  try {
    const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
    const path = `${EVOLUTION_CONFIG.ENDPOINTS.FETCH_PROFILE_PICTURE}/${encodeURIComponent(instanceName)}`;
    const res = await fetch(`${base}${path}`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ jid }),
    });
    const data = (await res.json().catch(() => null)) as { base64?: string; url?: string } | null;
    if (!res.ok || !data) {
      profilePictureCache.set(cacheKey, null);
      return null;
    }
    const url = data.url ?? (data.base64 ? `data:image/jpeg;base64,${data.base64}` : null) ?? null;
    profilePictureCache.set(cacheKey, url);
    return url;
  } catch {
    profilePictureCache.set(cacheKey, null);
    return null;
  }
}

/**
 * URLs do WhatsApp costumam apontar para blobs criptografados (.enc). O navegador baixa arquivo inútil se usar em `<img src>`.
 */
export function isLikelyUndisplayableWhatsAppMediaUrl(url: string): boolean {
  const u = String(url).trim();
  if (!u || u.startsWith("data:") || u.startsWith("blob:")) return false;
  if (!/^https?:\/\//i.test(u)) return false;
  const lower = u.toLowerCase();
  if (lower.includes(".enc") || lower.includes("file.enc") || lower.includes("type=enc")) return true;
  try {
    const path = new URL(u).pathname.toLowerCase();
    if (path.endsWith(".enc") || path.includes("/enc/")) return true;
  } catch {
    /* ignore */
  }
  return false;
}

/** Miniatura ou URL de mídia no payload Baileys / Evolution */
function extractMediaFromBaileysMessage(
  msg: Record<string, unknown>
): EvolutionMessage["media"] | undefined {
  const thumbToDataUrl = (b64: string, mime = "image/jpeg") => {
    const clean = b64.replace(/^data:image\/\w+;base64,/, "").trim();
    if (!clean) return undefined;
    return `data:${mime};base64,${clean}`;
  };

  const im = msg.imageMessage as Record<string, unknown> | undefined;
  if (im && typeof im === "object") {
    const jt = im.jpegThumbnail;
    if (typeof jt === "string" && jt.length > 0) {
      const dataUrl = thumbToDataUrl(jt);
      if (dataUrl) {
        return {
          type: "image",
          url: dataUrl,
          mimetype: typeof im.mimetype === "string" ? im.mimetype : "image/jpeg",
          filename: typeof im.fileName === "string" ? im.fileName : undefined,
        };
      }
    }
    const url = im.url;
    if (typeof url === "string" && (url.startsWith("http") || url.startsWith("https"))) {
      if (isLikelyUndisplayableWhatsAppMediaUrl(url)) {
        return {
          type: "image",
          mimetype: typeof im.mimetype === "string" ? im.mimetype : "image/jpeg",
          filename: typeof im.fileName === "string" ? im.fileName : undefined,
          requiresApiDecrypt: true,
        };
      }
      return {
        type: "image",
        url,
        mimetype: typeof im.mimetype === "string" ? im.mimetype : "image/jpeg",
        filename: typeof im.fileName === "string" ? im.fileName : undefined,
      };
    }
  }

  const vm = msg.videoMessage as Record<string, unknown> | undefined;
  if (vm && typeof vm === "object") {
    const jt = vm.jpegThumbnail;
    if (typeof jt === "string" && jt.length > 0) {
      const dataUrl = thumbToDataUrl(jt);
      if (dataUrl) return { type: "video", url: dataUrl, mimetype: "video/mp4" };
    }
    const url = vm.url;
    if (typeof url === "string" && (url.startsWith("http") || url.startsWith("https"))) {
      if (isLikelyUndisplayableWhatsAppMediaUrl(url)) {
        return {
          type: "video",
          mimetype: typeof vm.mimetype === "string" ? vm.mimetype : "video/mp4",
          requiresApiDecrypt: true,
        };
      }
      return {
        type: "video",
        url,
        mimetype: typeof vm.mimetype === "string" ? vm.mimetype : "video/mp4",
      };
    }
  }

  const st = msg.stickerMessage as Record<string, unknown> | undefined;
  if (st && typeof st === "object") {
    const jt = st.jpegThumbnail;
    if (typeof jt === "string" && jt.length > 0) {
      const dataUrl = thumbToDataUrl(jt);
      if (dataUrl) return { type: "sticker", url: dataUrl, mimetype: "image/webp" };
    }
    const url = st.url;
    if (typeof url === "string" && (url.startsWith("http") || url.startsWith("https"))) {
      if (isLikelyUndisplayableWhatsAppMediaUrl(url)) {
        return { type: "sticker", mimetype: typeof st.mimetype === "string" ? st.mimetype : "image/webp", requiresApiDecrypt: true };
      }
      return { type: "sticker", url, mimetype: typeof st.mimetype === "string" ? st.mimetype : "image/webp" };
    }
  }

  const am = msg.audioMessage as Record<string, unknown> | undefined;
  if (am && typeof am === "object") {
    const url = am.url;
    if (typeof url === "string" && (url.startsWith("http") || url.startsWith("https"))) {
      if (isLikelyUndisplayableWhatsAppMediaUrl(url)) {
        return {
          type: "audio",
          mimetype: typeof am.mimetype === "string" ? am.mimetype : "audio/ogg; codecs=opus",
          requiresApiDecrypt: true,
        };
      }
      return {
        type: "audio",
        url,
        mimetype: typeof am.mimetype === "string" ? am.mimetype : "audio/ogg; codecs=opus",
      };
    }
  }

  const dm = msg.documentMessage as Record<string, unknown> | undefined;
  if (dm && typeof dm === "object") {
    const mime = String(dm.mimetype || "");
    const fileName = typeof dm.fileName === "string" ? dm.fileName : undefined;
    if (mime.startsWith("image/")) {
      const djt = dm.jpegThumbnail;
      if (typeof djt === "string" && djt.length > 0) {
        const dataUrl = thumbToDataUrl(djt, mime);
        if (dataUrl) {
          return {
            type: "image",
            url: dataUrl,
            mimetype: mime || "image/jpeg",
            filename: fileName,
          };
        }
      }
      const url = dm.url;
      if (typeof url === "string" && (url.startsWith("http") || url.startsWith("https"))) {
        if (isLikelyUndisplayableWhatsAppMediaUrl(url)) {
          return {
            type: "image",
            mimetype: mime || "image/jpeg",
            filename: fileName,
            requiresApiDecrypt: true,
          };
        }
        return {
          type: "image",
          url,
          mimetype: mime || "image/jpeg",
          filename: fileName,
        };
      }
    }
    const url = dm.url;
    const docBase = {
      type: "document" as const,
      mimetype: mime || "application/octet-stream",
      filename: fileName,
    };
    if (typeof url === "string" && (url.startsWith("http") || url.startsWith("https"))) {
      if (isLikelyUndisplayableWhatsAppMediaUrl(url)) {
        return { ...docBase, requiresApiDecrypt: true };
      }
      return { ...docBase, url };
    }
    const djt = dm.jpegThumbnail;
    if (typeof djt === "string" && djt.length > 0) {
      const dataUrl = thumbToDataUrl(djt, mime || "application/pdf");
      if (dataUrl) {
        return { ...docBase, url: dataUrl, mimetype: mime || "application/pdf" };
      }
    }
    if (fileName || mime) {
      return { ...docBase, requiresApiDecrypt: true };
    }
  }

  return undefined;
}

/**
 * Evolution/Baileys envia `status`/`ack` como número (proto WebMessageInfo), não só string.
 * Ex.: Baileys recente — 0=ERROR, 1=PENDING, 2=SERVER_ACK, 3=DELIVERY_ACK, 4=READ, 5=PLAYED.
 * Versões antigas às vezes usam 0–3 com READ=3; priorizamos o esquema mais novo e fallback seguro.
 */
function mapEvolutionMessageAck(
  raw: unknown,
  fromMe: boolean
): EvolutionMessage["ack"] {
  if (raw === undefined || raw === null || raw === "") {
    return fromMe ? "sent" : "pending";
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const n = Math.trunc(raw);
    // Baileys típico: 1=PENDING, 2=SERVER_ACK, 3=DELIVERY, 4=READ, 5=PLAYED (0 pode ser ERROR ou legado)
    if (n <= 0) return "pending";
    if (n === 1) return "pending";
    if (n === 2) return "sent";
    if (n === 3) return "delivered";
    if (n >= 4) return "read";
    return "pending";
  }
  const s = String(raw).toLowerCase().replace(/_/g, "");
  if (s === "read" || s === "played") return "read";
  if (s === "delivered" || s === "deliveryack" || s === "received") return "delivered";
  if (s === "sent" || s === "serverack") return "sent";
  if (s === "failed" || s === "error") return "failed";
  const ackMap: Record<string, EvolutionMessage["ack"]> = {
    pending: "pending",
  };
  if (ackMap[s]) return ackMap[s];
  if (fromMe && /^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    if (!Number.isNaN(n)) return mapEvolutionMessageAck(n, fromMe);
  }
  return fromMe ? "sent" : "pending";
}

/** Mapeia um objeto de mensagem no formato Evolution API para EvolutionMessage */
function mapEvolutionMessageToOurFormat(item: unknown): EvolutionMessage | null {
  if (!item || typeof item !== "object") return null;
  const m = item as Record<string, unknown>;
  const key = m.key as Record<string, unknown> | undefined;
  const waMessageId =
    (key?.id as string) ?? (m.waMessageId as string) ?? (m.id as string) ?? "";
  const jid =
    (key?.remoteJid as string) ?? (m.jid as string) ?? (m.remoteJid as string) ?? "";
  const fromMe = Boolean(key?.fromMe ?? m.isFromMe ?? m.fromMe);
  const direction = fromMe ? "out" : "in";

  let text: string | undefined;
  if (typeof m.text === "string") text = m.text.trim() || undefined;
  else if (typeof m.body === "string") text = m.body.trim() || undefined;
  else if (m.message && typeof m.message === "object") {
    const msgPayload = unwrapBaileysMessagePayload(m.message as Record<string, unknown>);
    text = extractTextFromBaileysMessage(msgPayload, { allowMediaLabels: false });
  }

  let media: EvolutionMessage["media"] | undefined;
  if (m.message && typeof m.message === "object") {
    media = extractMediaFromBaileysMessage(
      unwrapBaileysMessagePayload(m.message as Record<string, unknown>)
    );
  }
  if (!media && typeof m.base64 === "string" && (m.message as Record<string, unknown>)?.imageMessage) {
    const mime = typeof m.mimetype === "string" ? m.mimetype : "image/jpeg";
    const clean = String(m.base64).replace(/^data:\w+\/\w+;base64,/, "").trim();
    if (clean) media = { type: "image", url: `data:${mime};base64,${clean}`, mimetype: mime };
  }
  if (!media && typeof m.base64 === "string" && (m.message as Record<string, unknown>)?.audioMessage) {
    const mimeRaw = typeof m.mimetype === "string" ? m.mimetype : "audio/ogg; codecs=opus";
    const mimeBase = (mimeRaw.split(";")[0] ?? "audio/ogg").trim() || "audio/ogg";
    const clean = String(m.base64).replace(/^data:\w+\/\w+;base64,/, "").trim();
    if (clean) media = { type: "audio", url: `data:${mimeBase};base64,${clean}`, mimetype: mimeRaw };
  }
  const flatUrl = m.mediaUrl ?? m.url;
  if (!media && typeof flatUrl === "string" && (flatUrl.startsWith("http") || flatUrl.startsWith("https"))) {
    const inner = (m.message as Record<string, unknown>) || {};
    const bad = isLikelyUndisplayableWhatsAppMediaUrl(flatUrl);
    if (inner.audioMessage) {
      const am = inner.audioMessage as Record<string, unknown>;
      const mt = typeof am.mimetype === "string" ? am.mimetype : "audio/ogg; codecs=opus";
      media = bad
        ? { type: "audio", mimetype: mt, requiresApiDecrypt: true }
        : { type: "audio", url: flatUrl, mimetype: mt };
    } else if (inner.videoMessage) {
      media = bad
        ? { type: "video", mimetype: "video/mp4", requiresApiDecrypt: true }
        : { type: "video", url: flatUrl, mimetype: "video/mp4" };
    } else if (inner.imageMessage || inner.stickerMessage) {
      media = bad
        ? { type: "image", mimetype: "image/jpeg", requiresApiDecrypt: true }
        : { type: "image", url: flatUrl, mimetype: "image/jpeg" };
    } else if (inner.documentMessage) {
      const dm = inner.documentMessage as Record<string, unknown>;
      const fn = typeof dm.fileName === "string" ? dm.fileName : undefined;
      const mime = typeof m.mimetype === "string" ? m.mimetype : "application/pdf";
      media = bad
        ? { type: "document", mimetype: mime, filename: fn, requiresApiDecrypt: true }
        : { type: "document", url: flatUrl, mimetype: mime, filename: fn };
    } else {
      const mime = typeof m.mimetype === "string" ? m.mimetype : "";
      if (mime.startsWith("audio/")) {
        media = bad
          ? { type: "audio", mimetype: mime, requiresApiDecrypt: true }
          : { type: "audio", url: flatUrl, mimetype: mime };
      } else {
        media = bad
          ? { type: "image", mimetype: mime || "image/jpeg", requiresApiDecrypt: true }
          : { type: "image", url: flatUrl, mimetype: mime || "image/jpeg" };
      }
    }
  }

  if (media?.url && isLikelyUndisplayableWhatsAppMediaUrl(media.url)) {
    const { url: _strip, ...rest } = media;
    media = { ...rest, requiresApiDecrypt: true };
  }

  if (media && text && SIDEBAR_PLACEHOLDER_TEXT.has(text)) {
    text =
      media.type === "document" && media.filename
        ? media.filename
        : undefined;
  }

  let timestamp = "";
  const ts = m.messageTimestamp ?? m.timestamp;
  if (typeof ts === "number") {
    // Evolution pode enviar em segundos ou milissegundos
    const ms = ts > 1e12 ? ts : ts * 1000;
    timestamp = new Date(ms).toISOString();
  } else if (typeof ts === "string") timestamp = ts;

  const rawStatus =
    m.status ??
    m.ack ??
    (key?.status as unknown) ??
    (m.update && typeof m.update === "object"
      ? (m.update as Record<string, unknown>).status
      : undefined);
  const ack = mapEvolutionMessageAck(rawStatus, fromMe);

  const whatsAppMessageKey =
    waMessageId && String((key?.remoteJid as string) || jid || "").includes("@")
      ? {
          id: waMessageId,
          remoteJid: String((key?.remoteJid as string) || jid),
          fromMe,
        }
      : undefined;

  const quoted =
    m.message && typeof m.message === "object"
      ? extractQuotedFromContext(
          extractBaileysContextInfo(m.message as Record<string, unknown>)
        )
      : undefined;

  return {
    waMessageId: waMessageId || `ev_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    jid,
    direction,
    text,
    media,
    timestamp,
    ack,
    isFromMe: fromMe,
    whatsAppMessageKey,
    quoted,
  };
}

/** Extrai array de mensagens do JSON da Evolution (inclui paginação `records`). */
function extractMessagesArray(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== "object") return [];
  const obj = raw as Record<string, unknown>;
  if (Array.isArray(obj.messages)) return obj.messages;
  if (Array.isArray(obj.records)) return obj.records;
  if (Array.isArray(obj.data)) return obj.data;
  const nested = obj.messages;
  if (nested && typeof nested === "object") {
    const n = nested as Record<string, unknown>;
    if (Array.isArray(n.records)) return n.records;
    if (Array.isArray(n.data)) return n.data;
  }
  const data = obj.data;
  if (data && typeof data === "object") {
    const d = data as Record<string, unknown>;
    if (Array.isArray(d.records)) return d.records;
    if (Array.isArray(d.messages)) return d.messages;
  }
  return [];
}

function normalizeEvolutionMessages(raw: unknown): EvolutionMessage[] {
  const arr = extractMessagesArray(raw);
  const out: EvolutionMessage[] = [];
  for (const item of arr) {
    const mapped = mapEvolutionMessageToOurFormat(item);
    if (mapped) out.push(mapped);
  }
  // Ordenar por timestamp para exibir histórico na ordem correta
  out.sort((a, b) => {
    const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return ta - tb;
  });
  return out;
}

async function fetchMessagesFromEvolution(
  instanceName: string,
  remoteJid: string,
  opts?: { limit?: number; before?: number }
): Promise<{ messages: EvolutionMessage[]; hasMore: boolean }> {
  const trimmed = String(remoteJid ?? "").trim();
  if (!trimmed) return { messages: [], hasMore: false };
  if (!trimmed.includes("@") && !trimmed.replace(/\D/g, "")) {
    return { messages: [], hasMore: false };
  }
  const limit = opts?.limit ?? 100;
  const before = opts?.before;

  if (shouldUseEvolutionBackendProxy()) {
    try {
      const data = await evolutionBackendRequest("GET", "/api/evolution/messages", {
        params: {
          instance: instanceName,
          remoteJid: trimmed,
          limit,
          ...(before != null && before > 0 ? { before } : {}),
        },
      });
      const payload =
        data && typeof data === "object" ? (data as Record<string, unknown>) : {};
      const msgs = normalizeEvolutionMessages(data);
      const hasMore = payload.hasMore === true;
      if (msgs.length > 0 || hasMore) return { messages: msgs, hasMore };
      if (!shouldUseEvolutionDirectFallback()) {
        return { messages: [], hasMore: false };
      }
    } catch (e) {
      if (!shouldUseEvolutionDirectFallback()) {
        console.warn("[DelivAI] backend /api/evolution/messages:", e);
        return { messages: [], hasMore: false };
      }
      console.warn("[DelivAI] backend /api/evolution/messages falhou, fallback direto:", e);
    }
  }

  if (!shouldUseEvolutionDirectFallback()) {
    return { messages: [], hasMore: false };
  }
  const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
  const headers = getAuthHeaders();
  const jid =
    trimmed && !trimmed.includes("@")
      ? `${trimmed.replace(/\D/g, "")}@s.whatsapp.net`
      : trimmed;
  const phoneJid =
    jidLooksLikeWhatsAppPhone(jid) && !jidIsPrivacyLid(jid)
      ? normalizeCoercePhoneJid(jid) ?? jid
      : undefined;
  const lidFromCache = phoneJid
    ? lookupLidForPhone(instanceName, phoneJid)
    : undefined;

  const pathV2 = `${EVOLUTION_CONFIG.ENDPOINTS.FIND_MESSAGES}/${encodeURIComponent(instanceName)}`;
  const pathV1 = `${EVOLUTION_CONFIG.ENDPOINTS.MESSAGES}/${encodeURIComponent(instanceName)}`;

  const doFetch = async (path: string, body: string): Promise<EvolutionMessage[] | null> => {
    const res = await fetch(`${base}${path}`, { method: "POST", headers, body });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return null;
    return normalizeEvolutionMessages(data);
  };

  const queryBodies: string[] = [
    JSON.stringify({
      where: { key: { remoteJid: jid } },
      page: 1,
      offset: limit,
    }),
  ];
  if (phoneJid && phoneJid !== jid) {
    queryBodies.push(
      JSON.stringify({
        where: { key: { remoteJidAlt: phoneJid } },
        page: 1,
        offset: limit,
      })
    );
    queryBodies.push(
      JSON.stringify({
        where: { key: { remoteJid: phoneJid } },
        page: 1,
        offset: limit,
      })
    );
  }
  if (lidFromCache && lidFromCache !== jid) {
    queryBodies.push(
      JSON.stringify({
        where: { key: { remoteJid: lidFromCache } },
        page: 1,
        offset: limit,
      })
    );
  }

  for (const body of queryBodies) {
    const msgs = await doFetch(pathV2, body);
    if (msgs && msgs.length > 0) {
      return { messages: msgs, hasMore: msgs.length >= limit };
    }
  }

  const bodyLegacy = JSON.stringify({
    remoteJid: jid,
    page: 1,
    offset: limit,
  });

  const legacyV2 = await doFetch(pathV2, bodyLegacy);
  if (legacyV2 && legacyV2.length > 0) {
    return { messages: legacyV2, hasMore: legacyV2.length >= limit };
  }

  const c = await doFetch(pathV1, bodyLegacy);
  return { messages: c ?? [], hasMore: (c?.length ?? 0) >= limit };
}

/** Une duas listas de mensagens por `waMessageId`, ordena por data (histórico tipo WhatsApp). */
export function mergeEvolutionMessages(
  prev: EvolutionMessage[],
  incoming: EvolutionMessage[]
): EvolutionMessage[] {
  const map = new Map<string, EvolutionMessage>();
  for (const m of prev) {
    if (m.waMessageId) map.set(m.waMessageId, m);
  }
  for (const m of incoming) {
    const id = m.waMessageId;
    if (!id) continue;
    const existing = map.get(id);
    map.set(id, existing ? { ...existing, ...m } : m);
  }
  const out = Array.from(map.values());
  out.sort((a, b) => {
    const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return ta - tb;
  });
  return out;
}

function messageTimeMs(m: EvolutionMessage): number {
  if (!m.timestamp) return 0;
  const t = new Date(m.timestamp).getTime();
  return Number.isFinite(t) ? t : 0;
}

function outgoingDedupeText(m: EvolutionMessage): string {
  const raw = m.text;
  const s = typeof raw === "string" ? raw : raw != null ? String(raw) : "";
  return s.trim().replace(/\s+/g, " ");
}

/**
 * Remove bolhas otimistas `temp_*` quando já existe a mensagem enviada vinda do servidor
 * (SSE / find messages), evitando duas linhas iguais até dar refresh.
 */
export function dedupeOptimisticOutgoingTemps(
  messages: EvolutionMessage[]
): EvolutionMessage[] {
  const isTempId = (id: string) => String(id).startsWith("temp_");
  const temps = messages.filter(
    (m) => m.isFromMe && m.waMessageId && isTempId(m.waMessageId)
  );
  if (temps.length === 0) return messages;

  const reals = messages.filter(
    (m) => m.isFromMe && m.waMessageId && !isTempId(m.waMessageId)
  );

  const usedRealIds = new Set<string>();
  const removeTempIds = new Set<string>();

  const sortedTemps = [...temps].sort(
    (a, b) => messageTimeMs(a) - messageTimeMs(b)
  );

  for (const t of sortedTemps) {
    const txt = outgoingDedupeText(t);
    if (!txt) continue;
    const tt = messageTimeMs(t);
    for (const r of reals) {
      if (usedRealIds.has(r.waMessageId)) continue;
      if (outgoingDedupeText(r) !== txt) continue;
      const rt = messageTimeMs(r);
      if (rt < tt - 120_000) continue;
      if (rt > tt + 300_000) continue;
      usedRealIds.add(r.waMessageId);
      removeTempIds.add(t.waMessageId);
      break;
    }
  }

  if (removeTempIds.size === 0) return messages;
  return messages.filter((m) => !removeTempIds.has(m.waMessageId));
}

export async function fetchMessagesFromN8n(
  instance: string,
  jid: string,
  opts?: { limit?: number; before?: number }
): Promise<{ messages: EvolutionMessage[]; hasMore: boolean }> {
  return fetchMessagesFromEvolution(instance, jid, opts);
}

function extractBase64FromEvolutionMediaResponse(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const o = data as Record<string, unknown>;
  if (typeof o.base64 === "string" && o.base64.length > 16) return o.base64;
  const nested = o.data;
  if (nested && typeof nested === "object") {
    const b = (nested as Record<string, unknown>).base64;
    if (typeof b === "string" && b.length > 16) return b;
  }
  const r = o.result;
  if (typeof r === "string" && r.length > 16) return r;
  return null;
}

/**
 * Obtém mídia descriptografada (imagem/áudio/vídeo) via Evolution API.
 * Corpo alinhado ao Postman **Evolution API v2.3** — `Chat` → `Get Base64 From Media Message`:
 * só `message.key.id` e `convertToMp4` (a instância já identifica o armazenamento da mensagem).
 * @see POST `/chat/getBase64FromMediaMessage/{instance}`
 */
export async function fetchEvolutionMediaAsDataUrl(
  instanceName: string,
  key: { id: string; remoteJid?: string; fromMe?: boolean },
  opts?: { convertToMp4?: boolean; mimeHint?: string }
): Promise<{ ok: boolean; dataUrl?: string; error?: string }> {
  const inst = String(instanceName || "").trim();
  const msgId = String(key.id || "").trim();
  if (!inst || !msgId) {
    return { ok: false, error: "ID da mensagem ausente." };
  }

  const messageKey: Record<string, unknown> = { id: msgId };
  if (key.remoteJid?.trim()) messageKey.remoteJid = key.remoteJid.trim();
  if (key.fromMe !== undefined) messageKey.fromMe = key.fromMe;

  if (shouldUseEvolutionBackendProxy()) {
    try {
      const data = (await evolutionBackendRequest("POST", "/api/evolution/media-base64", {
        body: {
          instance: inst,
          message: { key: messageKey },
          convertToMp4: opts?.convertToMp4 === true,
        },
      })) as { ok?: boolean; data?: unknown; error?: string };
      if (!data.ok) {
        return { ok: false, error: data.error || "Falha ao buscar mídia via backend." };
      }
      const raw = extractBase64FromEvolutionMediaResponse(data.data);
      if (!raw) {
        return { ok: false, error: "Resposta sem base64 da Evolution." };
      }
      if (raw.startsWith("data:")) {
        return { ok: true, dataUrl: raw };
      }
      const hint = (opts?.mimeHint ?? "image/jpeg").split(";")[0]!.trim() || "image/jpeg";
      return { ok: true, dataUrl: `data:${hint};base64,${raw}` };
    } catch (e) {
      if (!shouldUseEvolutionDirectFallback()) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : "Falha ao buscar mídia via backend.",
        };
      }
    }
  }

  if (!evolutionDirectReady()) {
    return { ok: false, error: "Evolution API não configurada." };
  }
  const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
  const path = `${EVOLUTION_CONFIG.ENDPOINTS.GET_BASE64_FROM_MEDIA_MESSAGE}/${encodeURIComponent(inst)}`;
  const body = {
    message: { key: messageKey },
    convertToMp4: opts?.convertToMp4 === true,
  };
  try {
    const res = await fetch(`${base}${path}`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const d = data as Record<string, unknown>;
      const msg =
        (typeof d.message === "string" ? d.message : null) ||
        (typeof d.error === "string" ? d.error : null) ||
        JSON.stringify(data).slice(0, 280);
      return { ok: false, error: msg || `HTTP ${res.status}` };
    }
    const raw = extractBase64FromEvolutionMediaResponse(data);
    if (!raw) {
      return { ok: false, error: "Resposta sem base64 da Evolution." };
    }
    if (raw.startsWith("data:")) {
      return { ok: true, dataUrl: raw };
    }
    const hint = (opts?.mimeHint ?? "image/jpeg").split(";")[0]!.trim() || "image/jpeg";
    return { ok: true, dataUrl: `data:${hint};base64,${raw}` };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : "Falha ao buscar mídia.",
    };
  }
}

/**
 * Marca mensagens como lidas na Evolution (chat aberto / atendimento).
 * Doc: POST `/chat/markMessageAsRead/{instance}` com `readMessages`.
 */
export async function markMessagesAsReadEvolution(
  instanceName: string,
  remoteJid: string,
  items: { id: string; fromMe: boolean }[]
): Promise<boolean> {
  if (!items.length) return false;
  const jid =
    remoteJid && remoteJid.includes("@")
      ? remoteJid
      : `${String(remoteJid).replace(/\D/g, "")}@s.whatsapp.net`;

  if (shouldUseEvolutionBackendProxy()) {
    try {
      for (const item of items) {
        await evolutionBackendRequest("POST", "/api/evolution/mark-read", {
          body: {
            instance: instanceName,
            remoteJid: jid,
            messageId: item.id,
            from_me: item.fromMe,
          },
        });
      }
      return true;
    } catch {
      /* fallback abaixo */
    }
  }

  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) return false;
  const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
  const readMessages = items.map((i) => ({
    remoteJid: jid,
    fromMe: i.fromMe,
    id: i.id,
  }));
  const path = `${EVOLUTION_CONFIG.ENDPOINTS.MARK_MESSAGE_READ}/${encodeURIComponent(instanceName)}`;
  try {
    const res = await fetch(`${base}${path}`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ readMessages }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export type EvolutionChatActionKey = {
  remoteJid: string;
  fromMe: boolean;
  id: string;
};

/**
 * Arquivar ou desarquivar conversa (Evolution API v2).
 * @see POST `/chat/archiveChat/{instance}`
 */
export async function evolutionArchiveChat(
  instanceName: string,
  chatJid: string,
  archive: boolean,
  lastKey: EvolutionChatActionKey
): Promise<{ ok: boolean; error?: string }> {
  const inst = String(instanceName || "").trim();
  const chat = String(chatJid || "").trim();
  if (!inst || !chat || !lastKey?.id) {
    return { ok: false, error: "Instância ou chat inválido." };
  }

  if (shouldUseEvolutionBackendProxy()) {
    try {
      const data = (await evolutionBackendRequest("POST", "/api/evolution/archive", {
        body: {
          instance: inst,
          remoteJid: lastKey.remoteJid || chat,
          chatJid: chat,
          archive,
          lastMessageKey: {
            id: String(lastKey.id).trim(),
            remoteJid: lastKey.remoteJid,
            fromMe: Boolean(lastKey.fromMe),
          },
        },
      })) as Record<string, unknown>;
      if (data.ok === false) {
        return { ok: false, error: String(data.error || "Falha ao arquivar.") };
      }
      return { ok: true };
    } catch (e) {
      return {
        ok: false,
        error: e instanceof Error ? e.message : "Falha ao arquivar via backend.",
      };
    }
  }

  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) {
    return { ok: false, error: "Evolution API não configurada." };
  }
  const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
  const path = `${EVOLUTION_CONFIG.ENDPOINTS.ARCHIVE_CHAT}/${encodeURIComponent(inst)}`;

  const normalizeChatJid = (j: string): string => {
    const t = String(j || "").trim();
    if (!t) return "";
    if (t.includes("@")) return t;
    const d = t.replace(/\D/g, "");
    return d ? `${d}@s.whatsapp.net` : "";
  };
  const chatCandidates = Array.from(
    new Set(
      [chat, lastKey.remoteJid, normalizeChatJid(chat), normalizeChatJid(lastKey.remoteJid)].filter(
        (x): x is string => Boolean(x)
      )
    )
  );
  if (chatCandidates.length === 0) {
    return { ok: false, error: "Chat inválido para arquivamento." };
  }

  const keyPayload = {
    remoteJid: lastKey.remoteJid,
    fromMe: Boolean(lastKey.fromMe),
    id: String(lastKey.id).trim(),
  };
  const payloadBuilders: Array<(chatValue: string) => string> = [
    // Formato da doc mais comum (v2)
    (chatValue) =>
      JSON.stringify({
        lastMessage: [{ key: keyPayload }],
        archive,
        chat: chatValue,
      }),
    // Algumas versões aceitam objeto em vez de array
    (chatValue) =>
      JSON.stringify({
        lastMessage: { key: keyPayload },
        archive,
        chat: chatValue,
      }),
    // Compat: algumas builds esperam sem a camada `key`
    (chatValue) =>
      JSON.stringify({
        lastMessage: [keyPayload],
        archive,
        chat: chatValue,
      }),
    (chatValue) =>
      JSON.stringify({
        lastMessage: keyPayload,
        archive,
        chat: chatValue,
      }),
  ];

  const send = async (body: string) =>
    fetch(`${base}${path}`, {
      method: "POST",
      headers: getAuthHeaders(),
      body,
    });
  try {
    let lastError = "Falha ao arquivar conversa.";
    for (const chatValue of chatCandidates) {
      for (const buildBody of payloadBuilders) {
        const res = await send(buildBody(chatValue));
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        if (res.ok) return { ok: true };
        const msg =
          (typeof data.message === "string" ? data.message : null) ||
          (typeof data.error === "string" ? data.error : null) ||
          JSON.stringify(data).slice(0, 220);
        lastError = msg || `HTTP ${res.status}`;
      }
    }
    return { ok: false, error: lastError };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : "Falha de rede.",
    };
  }
}

/**
 * Marcar conversa como não lida (Evolution API v2).
 * @see POST `/chat/markChatUnread/{instance}`
 */
export async function evolutionMarkChatUnread(
  instanceName: string,
  chatJid: string,
  lastKey: EvolutionChatActionKey
): Promise<{ ok: boolean; error?: string }> {
  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) {
    return { ok: false, error: "Evolution API não configurada." };
  }
  const inst = String(instanceName || "").trim();
  const chat = String(chatJid || "").trim();
  if (!inst || !chat) {
    return { ok: false, error: "Instância ou chat inválido." };
  }
  const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
  const path = `${EVOLUTION_CONFIG.ENDPOINTS.MARK_CHAT_UNREAD}/${encodeURIComponent(inst)}`;
  const body = JSON.stringify({
    lastMessage: [
      {
        remoteJid: lastKey.remoteJid,
        fromMe: lastKey.fromMe,
        id: lastKey.id,
      },
    ],
    chat,
  });
  try {
    const res = await fetch(`${base}${path}`, {
      method: "POST",
      headers: getAuthHeaders(),
      body,
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      const msg =
        (typeof data.message === "string" ? data.message : null) ||
        (typeof data.error === "string" ? data.error : null) ||
        JSON.stringify(data).slice(0, 220);
      return { ok: false, error: msg || `HTTP ${res.status}` };
    }
    return { ok: true };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : "Falha de rede.",
    };
  }
}

/**
 * Envio de texto direto na Evolution API (Postman: POST `/message/sendText/{instance}`,
 * body `{ number, text }` — número só dígitos com DDI, ex. 5511999999999).
 */
export async function sendTextMessage(
  instance: string,
  number: string,
  text: string
): Promise<{ ok: boolean; error?: string }> {
  const sendFailFallback =
    "Verifique a conexão do WhatsApp ou contate nosso suporte.";
  const inst = String(instance || "").trim();
  if (!inst) {
    return { ok: false, error: "Instância WhatsApp indisponível." };
  }
  const num = String(number || "").trim();
  if (!num) {
    return { ok: false, error: "Número inválido." };
  }
  const numberPayload = num.includes("@")
    ? num
    : num.replace(/\D/g, "");
  if (!numberPayload) {
    return { ok: false, error: "Número inválido." };
  }
  const bodyText = String(text || "").trim();
  if (!bodyText) {
    return { ok: false, error: "Mensagem vazia." };
  }

  if (shouldUseEvolutionBackendProxy()) {
    try {
      await evolutionBackendRequest("POST", "/api/evolution/send-text", {
        body: {
          instance: inst,
          cnpj: inst,
          to: numberPayload,
          phone: numberPayload,
          text: bodyText,
          mensagem: bodyText,
          message_category: "atendimento",
          sender_label: "Loja (DelivAI)",
        },
      });
      return { ok: true };
    } catch (e) {
      const raw = e instanceof Error ? e.message : "";
      return {
        ok: false,
        error: safeUserFacingMessage(raw, sendFailFallback),
      };
    }
  }

  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) {
    return { ok: false, error: sendFailFallback };
  }
  try {
    const url = evolutionInstanceUrl(EVOLUTION_CONFIG.ENDPOINTS.SEND_MESSAGE, inst);
    const res = await fetch(url, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ number: numberPayload, text: bodyText }),
    });
    if (res.ok) {
      return { ok: true };
    }
    return { ok: false, error: sendFailFallback };
  } catch {
    return { ok: false, error: sendFailFallback };
  }
}

export async function markMessageAsRead(
  instance: string,
  jid: string,
  messageId: string
): Promise<boolean> {
  try {
    const response = await fetch(buildN8nUrl(N8N_CONFIG.ENDPOINTS.MARK_READ), {
      method: "POST",
      headers: getN8nHeaders(),
      body: JSON.stringify({
        instance,
        remoteJid: jid,
        messageId,
      }),
    });

    return response.ok;
  } catch (error) {
    console.error("Error marking message as read:", error);
    return false;
  }
}

// URL do webhook Evolution no n8n (mesmo usado no nó "Cria Instancia")
function getEvolutionWebhookUrl(): string {
  const base = N8N_WEBHOOK_URL.replace(/\/$/, "");
  return `${base}/evolution-v1`;
}

/**
 * URL que a Evolution API deve usar ao criar/configurar webhook (alcançável pelo **servidor** Evolution).
 * Use `REACT_APP_EVOLUTION_N8N_WEBHOOK_URL` com a URL pública completa (ex.: https://seu-dominio/webhook/evolution-v1
 * ou o path que o proxy expõe, ex. .../api/n8n/evolution-v1). Sem isso, cai em `N8N_WEBHOOK_URL` + evolution-v1
 * (pode gerar URL interna tipo `http://delivai_n8n:5678/...` inacessível fora do Docker).
 */
export function getEvolutionWebhookUrlForApi(): string {
  const backendDerived = BACKEND_URL
    ? `${BACKEND_URL.replace(/\/$/, "")}/webhook/evolution-v1`
    : "";
  const raw =
    typeof process !== "undefined" && process.env?.REACT_APP_EVOLUTION_N8N_WEBHOOK_URL
      ? String(process.env.REACT_APP_EVOLUTION_N8N_WEBHOOK_URL).trim()
      : "";

  if (!isDev && DELIVAI_ENV === "production") {
    if (raw && /delivai-test/i.test(raw)) {
      console.warn(
        "[DelivAI] REACT_APP_EVOLUTION_N8N_WEBHOOK_URL aponta para TEST em produção; usando BACKEND_URL."
      );
      if (backendDerived) return backendDerived;
    }
    if (backendDerived) return backendDerived;
  }

  if (raw) return raw.replace(/\/$/, "");
  return getEvolutionWebhookUrl();
}

export type EvolutionWebhookEnvironment = "test" | "prod";

/** Instância interna DelivAI — única com lease test→prod (2h) no backend. */
export const DELIVAI_WEBHOOK_LEASE_INSTANCE = "99999999000199";

export function isEvolutionWebhookLeaseEligible(instanceName: string): boolean {
  const digits = String(instanceName ?? "").replace(/\D/g, "");
  return digits === DELIVAI_WEBHOOK_LEASE_INSTANCE;
}

/** Renova lease de 2h no backend (só instância DelivAI). */
export async function touchEvolutionWebhookLease(instance: string): Promise<void> {
  if (!shouldUseEvolutionBackendProxy() || !isEvolutionWebhookLeaseEligible(instance)) return;
  try {
    await evolutionBackendRequest("POST", "/api/evolution/webhook-lease/touch", {
      body: { instance: instance.replace(/\D/g, "") },
    });
  } catch {
    /* opcional */
  }
}

export type StartEvolutionWebhookLeaseOpts = EvolutionPanelRequestOpts & {
  /**
   * false = só TTL Redis no backend test (webhook já gravado na Evolution da visão prod).
   * Default true (legado: backend test também aplica webhook na Evolution ligada a ele).
   */
  applyWebhook?: boolean;
};

export async function startEvolutionWebhookLease(
  instance: string,
  opts?: StartEvolutionWebhookLeaseOpts
): Promise<{ ok: boolean; error?: string }> {
  if (!shouldUseEvolutionBackendProxy()) {
    return { ok: false, error: "BACKEND_URL não configurado" };
  }
  try {
    const applyWebhook = opts?.applyWebhook !== false;
    const data = (await evolutionBackendRequest("POST", "/api/evolution/webhook-lease/start", {
      body: {
        instance: instance.replace(/\D/g, ""),
        applyWebhook,
      },
      backendBaseUrl: backendBaseFromPanelOpts(opts) ?? evolutionBackendBaseForView("test"),
    })) as { ok?: boolean; error?: string };
    return { ok: data.ok === true, error: data.error };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

export async function clearEvolutionWebhookLease(
  instance: string,
  opts?: EvolutionPanelRequestOpts
): Promise<{ ok: boolean; error?: string }> {
  if (!shouldUseEvolutionBackendProxy()) {
    return { ok: false, error: "BACKEND_URL não configurado" };
  }
  try {
    const data = (await evolutionBackendRequest("POST", "/api/evolution/webhook-lease/clear", {
      body: { instance: instance.replace(/\D/g, "") },
      backendBaseUrl: backendBaseFromPanelOpts(opts) ?? evolutionBackendBaseForView("prod"),
    })) as { ok?: boolean; error?: string };
    return { ok: data.ok === true, error: data.error };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

const EVOLUTION_INBOUND_WEBHOOK_PATH = "/webhook/evolution-v1";

/**
 * URL interna Docker + Host público (Tomcat rejeita underscore no Host).
 * Evolution (projeto delival) → backend test/prod — ver docs/integrations/whatsapp-evolution.md.
 */
const EVOLUTION_WEBHOOK_DOCKER_TEST = {
  url: "http://delivai-test_delivai-backend-sandbox:8080/webhook/evolution-v1",
  hostHeader: "delivai-test-delivai-backend-sandbox.rwysej.easypanel.host",
} as const;

const EVOLUTION_WEBHOOK_DOCKER_PROD = {
  url: "http://delivai-prod_delivai-backend:8080/webhook/evolution-v1",
  hostHeader: "delivai-prod-delivai-backend.rwysej.easypanel.host",
} as const;

function evolutionWebhookFromBackendBase(base: string): string {
  const b = trimTrailingSlash(base.trim());
  return b ? `${b}${EVOLUTION_INBOUND_WEBHOOK_PATH}` : "";
}

/** Headers extras no POST do webhook Evolution (auth do backend + Host quando URL interna). */
export function buildEvolutionWebhookDeliveryHeaders(
  webhookUrl: string
): Record<string, string> | undefined {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (N8N_AUTH_HEADER) {
    headers.authorization = N8N_AUTH_HEADER;
  }
  const host = resolveEvolutionWebhookHostHeader(webhookUrl);
  if (host) {
    headers.Host = host;
  }
  return Object.keys(headers).length > 0 ? headers : undefined;
}

function resolveEvolutionWebhookHostHeader(webhookUrl: string): string | null {
  const u = webhookUrl.trim().toLowerCase();
  if (u.includes("delivai-test_delivai-backend-sandbox")) {
    return EVOLUTION_WEBHOOK_DOCKER_TEST.hostHeader;
  }
  if (u.includes("delivai-prod_delivai-backend")) {
    return EVOLUTION_WEBHOOK_DOCKER_PROD.hostHeader;
  }
  return null;
}

function normalizeWebhookUrlForCompare(url: string): string {
  return url.trim().replace(/\/$/, "").toLowerCase();
}

/**
 * URL do webhook Evolution → backend.
 * - Mesmo ambiente da visão: URL **Docker interna** (rede EasyPanel do projeto).
 * - Cross-env (visão test → dest prod ou vice-versa): URL **pública HTTPS** —
 *   stacks isolados não resolvem hostname Docker do outro projeto (motivo da Evolution por ambiente).
 */
export function getEvolutionWebhookUrlForTarget(
  target: EvolutionWebhookEnvironment,
  viewEnv?: EvolutionWebhookEnvironment
): string | null {
  const sameEnv = !viewEnv || viewEnv === target;
  if (sameEnv) {
    return target === "test"
      ? EVOLUTION_WEBHOOK_DOCKER_TEST.url
      : EVOLUTION_WEBHOOK_DOCKER_PROD.url;
  }
  const publicBase = getEvolutionViewBackendUrls()[target];
  const url = evolutionWebhookFromBackendBase(publicBase);
  return url || null;
}

export function getEvolutionWebhookPresets(
  viewEnv?: EvolutionWebhookEnvironment
): Record<EvolutionWebhookEnvironment, string | null> {
  return {
    test: getEvolutionWebhookUrlForTarget("test", viewEnv),
    prod: getEvolutionWebhookUrlForTarget("prod", viewEnv),
  };
}

/** Backends públicos test/prod para visão Evolution no painel Dev. */
export function getEvolutionViewBackendUrls(): Record<
  EvolutionWebhookEnvironment,
  string
> {
  const test = (
    process.env.REACT_APP_BACKEND_URL_TEST ||
    process.env.REACT_APP_BACKEND_URL_STAGING ||
    "https://delivai-test-delivai-backend-sandbox.rwysej.easypanel.host"
  )
    .trim()
    .replace(/\/$/, "");
  const prod = (
    process.env.REACT_APP_BACKEND_URL_PRODUCTION ||
    "https://delivai-prod-delivai-backend.rwysej.easypanel.host"
  )
    .trim()
    .replace(/\/$/, "");
  return { test, prod };
}

/** Ambiente “nativo” deste build (BACKEND_URL atual). */
export function detectPanelEvolutionViewEnv(): EvolutionWebhookEnvironment {
  const b = (BACKEND_URL || "").toLowerCase();
  if (b.includes("delivai-prod") || /\/prod\b/.test(b) || b.includes("production")) {
    return "prod";
  }
  return "test";
}

/**
 * Base do proxy Kotlin para a visão escolhida.
 * No npm start, visão = ambiente local usa `/api/delivai-backend`; o outro usa URL pública.
 */
export function evolutionBackendBaseForView(
  view: EvolutionWebhookEnvironment
): string {
  const native = detectPanelEvolutionViewEnv();
  if (isDev && view === native && BACKEND_URL?.trim()) {
    return "/api/delivai-backend";
  }
  return getEvolutionViewBackendUrls()[view];
}

export type EvolutionPanelRequestOpts = {
  viewEnv?: EvolutionWebhookEnvironment;
};

function backendBaseFromPanelOpts(
  opts?: EvolutionPanelRequestOpts
): string | undefined {
  if (!opts?.viewEnv) return undefined;
  return evolutionBackendBaseForView(opts.viewEnv);
}

/** Instância open/connected na lista Evolution. */
export function isEvolutionInstanceConnectedRow(row: unknown): boolean {
  if (!row || typeof row !== "object") return false;
  const o = row as Record<string, unknown>;
  const nested =
    o.instance && typeof o.instance === "object"
      ? (o.instance as Record<string, unknown>)
      : o;
  const status = String(
    nested.connectionStatus ?? nested.state ?? o.connectionStatus ?? o.state ?? ""
  )
    .toLowerCase()
    .trim();
  return status === "open" || status === "connected";
}

export function detectEvolutionWebhookTarget(
  url: string,
  viewEnv?: EvolutionWebhookEnvironment
): EvolutionWebhookEnvironment | "custom" | null {
  const normalized = normalizeWebhookUrlForCompare(url);
  if (!normalized) return null;
  const presetSets = [
    getEvolutionWebhookPresets(viewEnv),
    getEvolutionWebhookPresets("test"),
    getEvolutionWebhookPresets("prod"),
  ];
  for (const presets of presetSets) {
    for (const env of ["test", "prod"] as const) {
      const preset = presets[env];
      if (preset && normalizeWebhookUrlForCompare(preset) === normalized) {
        return env;
      }
    }
  }
  const urls = getEvolutionViewBackendUrls();
  const publicTest = normalizeWebhookUrlForCompare(
    evolutionWebhookFromBackendBase(urls.test)
  );
  const publicProd = normalizeWebhookUrlForCompare(
    evolutionWebhookFromBackendBase(urls.prod)
  );
  if (publicTest && normalized === publicTest) return "test";
  if (publicProd && normalized === publicProd) return "prod";
  if (/delivai-test/i.test(normalized) || /sandbox|staging/i.test(normalized)) return "test";
  if (/delivai-prod/i.test(normalized) && !/test|sandbox|staging/i.test(normalized)) return "prod";
  return "custom";
}


/**
 * Cria uma instância na Evolution API (mesmo payload do nó n8n "Cria Instancia").
 * Usa REACT_APP_EVOLUTION_API_URL e REACT_APP_EVOLUTION_API_KEY.
 * Se `webhookUrl` não for passado ou estiver vazio, usa `getEvolutionWebhookUrlForApi()` (.env / fallback).
 * @param instanceName - Nome da instância (ex.: CNPJ com 14 dígitos)
 * @param number - Telefone com DDD, só dígitos (será prefixado com 55)
 */
export async function createEvolutionInstance(
  instanceName: string,
  number: string,
  options?: { webhookUrl?: string; viewEnv?: EvolutionWebhookEnvironment }
): Promise<{ ok: boolean; error?: string; data?: unknown }> {
  const phone = cleanPhoneFormat(number);
  const customWh = typeof options?.webhookUrl === "string" ? options.webhookUrl.trim() : "";
  const webhookUrlResolved = customWh
    ? customWh.replace(/\/$/, "")
    : getEvolutionWebhookUrlForApi();
  const panelOpts: EvolutionPanelRequestOpts | undefined = options?.viewEnv
    ? { viewEnv: options.viewEnv }
    : undefined;

  if (shouldUseEvolutionBackendProxy()) {
    try {
      const data = (await evolutionBackendRequest("POST", "/api/evolution/create-instance", {
        body: {
          instanceName,
          number: phone,
          ...(webhookUrlResolved ? { webhookUrl: webhookUrlResolved } : {}),
        },
        backendBaseUrl: backendBaseFromPanelOpts(panelOpts),
      })) as { ok?: boolean; data?: unknown; error?: string };
      if (data.ok) return { ok: true, data: data.data };
      return { ok: false, error: data.error || "Falha ao criar instância via backend", data: data.data };
    } catch (e) {
      if (!shouldUseEvolutionDirectFallback() || options?.viewEnv) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : "Falha ao criar instância via backend",
        };
      }
    }
  }

  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) {
    return {
      ok: false,
      error: "Credenciais da Evolution API não configuradas (REACT_APP_EVOLUTION_API_URL / API_KEY)",
    };
  }
  const numberWithCountry = phone ? `55${phone}` : "";
  try {
    const body = {
      instanceName,
      number: numberWithCountry,
      qrcode: true,
      integration: "WHATSAPP-BAILEYS",
      rejectCall: true,
      groupsIgnore: true,
      alwaysOnline: true,
      readMessages: true,
      readStatus: true,
      webhook: {
        url: webhookUrlResolved,
        byEvents: false,
        base64: true,
        headers: {
          authorization: N8N_AUTH_HEADER,
          "Content-Type": "application/json",
        },
        events: [
          "MESSAGES_UPSERT",
          "MESSAGES_UPDATE",
          "SEND_MESSAGE",
          "CHATS_UPSERT",
          "CHATS_UPDATE",
          "CONNECTION_UPDATE",
        ],
      },
    };
    const url = buildApiUrl(EVOLUTION_CONFIG.ENDPOINTS.CREATE_INSTANCE);
    const response = await fetch(url, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        ok: false,
        error: (data?.message || data?.error) || `HTTP ${response.status}`,
        data,
      };
    }
    return { ok: true, data };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Erro ao criar instância";
    console.error("createEvolutionInstance:", error);
    return { ok: false, error: message };
  }
}

/**
 * Configura o webhook de uma instância existente para a URL do n8n.
 * Assim a Evolution envia MESSAGES_UPSERT, CHATS_UPSERT, etc. em tempo real para o n8n (armazenamento).
 * Útil quando a instância foi criada pelo Evolution Manager sem webhook ou com URL errada.
 */
export async function setEvolutionWebhook(
  instanceName: string
): Promise<{ ok: boolean; error?: string }> {
  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) {
    return { ok: false, error: "Credenciais da Evolution API não configuradas" };
  }
  const webhookUrl = getEvolutionWebhookUrlForApi();
  if (!webhookUrl) {
    return {
      ok: false,
      error:
        "Defina REACT_APP_EVOLUTION_N8N_WEBHOOK_URL (URL pública do webhook n8n) ou REACT_APP_N8N_WEBHOOK_URL no .env",
    };
  }
  try {
    const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
    const path = `${EVOLUTION_CONFIG.ENDPOINTS.SET_WEBHOOK}/${encodeURIComponent(instanceName)}`;
    const extraHeaders = buildEvolutionWebhookDeliveryHeaders(webhookUrl);
    const body = buildEvolutionWebhookSetPayload(
      true,
      webhookUrl,
      false,
      true,
      [
        "MESSAGES_UPSERT",
        "MESSAGES_UPDATE",
        "SEND_MESSAGE",
        "CHATS_UPSERT",
        "CHATS_UPDATE",
        "CONNECTION_UPDATE",
      ],
      extraHeaders
    );
    const res = await fetch(`${base}${path}`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        error: parseEvolutionApiErrorResponse(data, res.status),
      };
    }
    return { ok: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Erro ao configurar webhook";
    console.error("setEvolutionWebhook:", error);
    return { ok: false, error: message };
  }
}

/**
 * Normaliza estado Evolution/backend: `connected` → `open` (painel usa `whatsConnected`).
 */
export function normalizeEvolutionConnectionState(
  state: string | undefined | null
): string | undefined {
  const raw = state?.trim().toLowerCase();
  if (!raw) return undefined;
  if (raw === "connected") return "open";
  return raw;
}

/**
 * Consulta o status de uma instância na Evolution API.
 */
export async function getEvolutionInstanceStatus(
  instanceName: string
): Promise<{ ok: boolean; state?: string; error?: string }> {
  if (shouldUseEvolutionBackendProxy()) {
    try {
      const data = (await evolutionBackendRequest("GET", "/api/evolution/connection-state", {
        params: { instance: instanceName },
      })) as { ok?: boolean; state?: string; error?: string };
      const normalized = normalizeEvolutionConnectionState(data.state);
      if (data.ok && normalized) return { ok: true, state: normalized };
      if (!shouldUseEvolutionDirectFallback()) {
        return {
          ok: false,
          error: data.error || "Falha ao consultar status via backend",
        };
      }
    } catch (e) {
      if (!shouldUseEvolutionDirectFallback()) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : "Falha ao consultar status via backend",
        };
      }
    }
  }

  if (!evolutionDirectReady()) {
    return { ok: false, error: "Credenciais da Evolution API não configuradas" };
  }
  try {
    const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
    const path = `${EVOLUTION_CONFIG.ENDPOINTS.CONNECTION_STATE}/${encodeURIComponent(instanceName)}`;
    const url = `${base}${path}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: data?.message || `HTTP ${res.status}` };
    const state = normalizeEvolutionConnectionState(
      data?.instance?.state ?? data?.state ?? data?.connectionStatus
    );
    return { ok: true, state };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : "Erro ao consultar status",
    };
  }
}

function evolutionInstanceUrl(endpoint: string, instanceName: string): string {
  const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
  return `${base}${endpoint}/${encodeURIComponent(instanceName)}`;
}

/** Garante que a resposta seja JSON; se vier HTML (erro de proxy/CORS), lança erro claro. */
async function parseJsonOrThrow(res: Response, context: string): Promise<Record<string, unknown>> {
  const ct = res.headers.get("content-type") || "";
  const text = await res.text();
  if (ct.includes("text/html") || text.trimStart().startsWith("<")) {
    const hint =
      process.env.NODE_ENV === "development"
        ? " Em desenvolvimento, adicione REACT_APP_USE_PROXY=true no .env e reinicie o npm start para usar o proxy e evitar CORS."
        : "";
    throw new Error(`${context}: a API respondeu com HTML em vez de JSON.${hint}`);
  }
  try {
    return (text ? JSON.parse(text) : {}) as Record<string, unknown>;
  } catch {
    throw new Error(`${context}: resposta inválida (não é JSON).`);
  }
}

function parseQrFromResponse(data: Record<string, unknown>): string | null {
  const result = data?.result as Record<string, unknown> | undefined;
  const raw =
    data?.base64 ??
    data?.code ??
    result?.base64 ??
    result?.code ??
    (typeof data?.result === "string" ? data.result : null);
  if (!raw || typeof raw !== "string") return null;
  return raw.startsWith("data:") ? raw : `data:image/png;base64,${raw}`;
}

/**
 * Dispara a geração do QR e obtém o QR code (Evolution API v2: /instance/qrcode pode retornar 404;
 * tenta /instance/connect primeiro e extrai QR da resposta, depois caminhos alternativos).
 */
export async function connectEvolutionInstance(
  instanceName: string
): Promise<{ ok: boolean; error?: string; qrBase64?: string }> {
  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) {
    return { ok: false, error: "Credenciais da Evolution API não configuradas" };
  }
  const url = evolutionInstanceUrl(EVOLUTION_CONFIG.ENDPOINTS.INSTANCE_CONNECT, instanceName);
  const res = await fetch(url, { headers: getAuthHeaders() });
  let data: Record<string, unknown>;
  try {
    data = await parseJsonOrThrow(res, "Evolution connect");
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "Resposta inválida da API" };
  }
  if (!res.ok) {
    const msg = (data?.message as string) || (res.status === 404 ? "Instância não encontrada (404). Crie a instância com o CNPJ no Evolution Manager ou use o botão \"Criar instância\" nesta tela." : `HTTP ${res.status}`);
    return { ok: false, error: msg };
  }
  const qrBase64 = parseQrFromResponse(data);
  return { ok: true, qrBase64: qrBase64 || undefined };
}

/**
 * Obtém o QR code da instância (tenta connect primeiro; se não vier QR, tenta /instance/qrcode e /instance/fetchQrCode).
 */
export async function getEvolutionQrCode(
  instanceName: string,
  opts?: EvolutionPanelRequestOpts
): Promise<{ ok: boolean; base64?: string; error?: string }> {
  if (shouldUseEvolutionBackendProxy()) {
    try {
      const data = (await evolutionBackendRequest("GET", "/api/evolution/qr-code", {
        params: { instance: instanceName },
        timeoutMs: 30_000,
        backendBaseUrl: backendBaseFromPanelOpts(opts),
      })) as { ok?: boolean; base64?: string; error?: string };
      if (data.ok && data.base64) return { ok: true, base64: data.base64 };
      return { ok: false, error: data.error || "Não foi possível gerar o QR code via backend" };
    } catch (e) {
      if (!shouldUseEvolutionDirectFallback() || opts?.viewEnv) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : "Erro ao obter QR via backend",
        };
      }
    }
  }

  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) {
    return { ok: false, error: "Credenciais da Evolution API não configuradas" };
  }
  const headers = getAuthHeaders();

  // 1) Chamar connect; em algumas versões o QR vem na resposta
  const connectResult = await connectEvolutionInstance(instanceName);
  if (connectResult.qrBase64) return { ok: true, base64: connectResult.qrBase64 };
  if (!connectResult.ok) return { ok: false, error: connectResult.error };

  // 2) Tentar GET /instance/qrcode/{instance}
  try {
    const qrcodeUrl = evolutionInstanceUrl(EVOLUTION_CONFIG.ENDPOINTS.QR_CODE, instanceName);
    const res = await fetch(qrcodeUrl, { headers });
    const data = await res.json().catch(() => ({})) as Record<string, unknown>;
    if (res.ok) {
      const src = parseQrFromResponse(data);
      if (src) return { ok: true, base64: src };
    }
    if (res.status === 404) {
      const fetchUrl = evolutionInstanceUrl(EVOLUTION_CONFIG.ENDPOINTS.FETCH_QR_CODE, instanceName);
      const res2 = await fetch(fetchUrl, { headers });
      const data2 = (await res2.json().catch(() => ({}))) as Record<string, unknown>;
      if (res2.ok) {
        const src = parseQrFromResponse(data2);
        if (src) return { ok: true, base64: src };
      }
    }
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : "Erro ao obter QR. Use REACT_APP_USE_PROXY=true no .env e reinicie o npm start.",
    };
  }

  return {
    ok: false,
    error:
      "QR não retornado pela API. Conecte pelo Evolution Manager (aba do navegador) com o mesmo CNPJ ou verifique a versão da Evolution API.",
  };
}

/**
 * Desconecta (logout) a instância do WhatsApp.
 * Evolution 2.x: DELETE /instance/logout/{name}. Erro user-facing sem IP/URL.
 */
export async function disconnectEvolutionInstance(
  instanceName: string
): Promise<{ ok: boolean; error?: string }> {
  const failMsg = "Verifique a conexão do WhatsApp ou contate nosso suporte.";
  if (shouldUseEvolutionBackendProxy()) {
    try {
      const data = (await evolutionBackendRequest("DELETE", "/api/evolution/disconnect", {
        params: { instance: instanceName },
      })) as { ok?: boolean; error?: string };
      if (data.ok) return { ok: true };
      return {
        ok: false,
        error: safeUserFacingMessage(data.error, failMsg),
      };
    } catch (e) {
      if (!shouldUseEvolutionDirectFallback()) {
        return {
          ok: false,
          error: safeUserFacingMessage(
            e instanceof Error ? e.message : "",
            failMsg
          ),
        };
      }
    }
  }

  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) {
    return { ok: false, error: failMsg };
  }
  try {
    const url = evolutionInstanceUrl(EVOLUTION_CONFIG.ENDPOINTS.INSTANCE_DISCONNECT, instanceName);
    const res = await fetch(url, { method: "DELETE", headers: getAuthHeaders() });
    if (!res.ok) {
      // Legado: algumas versões usavam /instance/disconnect
      if (res.status === 404) {
        const legacy = evolutionInstanceUrl("/instance/disconnect", instanceName);
        const res2 = await fetch(legacy, { method: "DELETE", headers: getAuthHeaders() });
        if (res2.ok) return { ok: true };
      }
      return { ok: false, error: failMsg };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: failMsg };
  }
}

/** Lista instâncias na Evolution API (GET fetchInstances). */
export async function listEvolutionInstances(
  opts?: EvolutionPanelRequestOpts
): Promise<{
  ok: boolean;
  instances?: unknown[];
  error?: string;
}> {
  if (shouldUseEvolutionBackendProxy()) {
    try {
      const data = (await evolutionBackendRequest("GET", "/api/evolution/instances", {
        backendBaseUrl: backendBaseFromPanelOpts(opts),
      })) as {
        ok?: boolean;
        data?: unknown;
        error?: string;
      };
      if (!data.ok) {
        return { ok: false, error: data.error || "Falha ao listar instâncias via backend" };
      }
      const raw = data.data;
      let instances: unknown[] = [];
      if (Array.isArray(raw)) {
        instances = raw;
      } else if (raw && typeof raw === "object") {
        const o = raw as Record<string, unknown>;
        const nested = o.instance ?? o.instances ?? o.data;
        if (Array.isArray(nested)) instances = nested;
      }
      return { ok: true, instances };
    } catch (e) {
      if (!shouldUseEvolutionDirectFallback() || opts?.viewEnv) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : "Erro ao listar instâncias via backend",
        };
      }
    }
  }

  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) {
    return { ok: false, error: "Credenciais da Evolution API não configuradas" };
  }
  try {
    const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
    const url = `${base}${EVOLUTION_CONFIG.ENDPOINTS.FETCH_INSTANCES}`;
    const res = await fetch(url, { method: "GET", headers: getAuthHeaders() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        error:
          (typeof (data as { message?: string }).message === "string"
            ? (data as { message: string }).message
            : null) || `HTTP ${res.status}`,
      };
    }
    const raw = data as unknown;
    let instances: unknown[] = [];
    if (Array.isArray(raw)) {
      instances = raw;
    } else if (raw && typeof raw === "object") {
      const o = raw as Record<string, unknown>;
      const nested = o.instance ?? o.instances ?? o.data;
      if (Array.isArray(nested)) instances = nested;
    }
    return { ok: true, instances };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : "Erro ao listar instâncias",
    };
  }
}

/** DELETE /instance/delete/{instanceName} — remove a instância na Evolution API. */
export async function deleteEvolutionInstance(
  instanceName: string,
  opts?: EvolutionPanelRequestOpts
): Promise<{ ok: boolean; error?: string }> {
  const inst = String(instanceName || "").trim();
  if (!inst) {
    return { ok: false, error: "Nome da instância inválido" };
  }

  if (shouldUseEvolutionBackendProxy()) {
    try {
      const data = (await evolutionBackendRequest("DELETE", "/api/evolution/instance", {
        params: { instance: inst },
        backendBaseUrl: backendBaseFromPanelOpts(opts),
      })) as { ok?: boolean; error?: string };
      if (data.ok) return { ok: true };
      return { ok: false, error: data.error || "Falha ao excluir instância via backend" };
    } catch (e) {
      if (!shouldUseEvolutionDirectFallback() || opts?.viewEnv) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : "Erro ao excluir instância via backend",
        };
      }
    }
  }

  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) {
    return { ok: false, error: "Credenciais da Evolution API não configuradas" };
  }
  try {
    const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
    const url = `${base}${EVOLUTION_CONFIG.ENDPOINTS.DELETE_INSTANCE}/${encodeURIComponent(inst)}`;
    const res = await fetch(url, { method: "DELETE", headers: getAuthHeaders() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, error: parseEvolutionApiErrorResponse(data, res.status) };
    }
    return { ok: true };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : "Erro ao excluir instância",
    };
  }
}

export const EVOLUTION_DEFAULT_WEBHOOK_EVENTS = [
  "MESSAGES_UPSERT",
  "MESSAGES_UPDATE",
  "SEND_MESSAGE",
  "CHATS_UPSERT",
  "CHATS_UPDATE",
  "CONNECTION_UPDATE",
] as const;

/**
 * Lista ampla de eventos para o painel (alinhada ao Manager / Evolution v2.1+ e extras v2.3.x).
 * O servidor valida contra o enum da versão instalada — se rejeitar um evento, remova-o no painel.
 */
export const EVOLUTION_WEBHOOK_EVENTS_ALL: readonly string[] = [
  "APPLICATION_STARTUP",
  "CALL",
  "CHATS_DELETE",
  "CHATS_SET",
  "CHATS_UPDATE",
  "CHATS_UPSERT",
  "CONNECTION_UPDATE",
  "CONTACTS_SET",
  "CONTACTS_UPDATE",
  "CONTACTS_UPSERT",
  "GROUPS_UPSERT",
  "GROUP_UPDATE",
  "GROUP_PARTICIPANTS_UPDATE",
  "INSTANCE_CREATE",
  "INSTANCE_DELETE",
  "LABELS_ASSOCIATION",
  "LABELS_EDIT",
  "LOGOUT_INSTANCE",
  "MESSAGES_DELETE",
  "MESSAGES_EDITED",
  "MESSAGES_SET",
  "MESSAGES_UPDATE",
  "MESSAGES_UPSERT",
  "NEW_JWT_TOKEN",
  "PRESENCE_UPDATE",
  "QRCODE_UPDATED",
  "REMOVE_INSTANCE",
  "SEND_MESSAGE",
  "SEND_MESSAGE_UPDATE",
  "STATUS_INSTANCE",
  "TYPEBOT_CHANGE_STATUS",
  "TYPEBOT_START",
].sort((a, b) => a.localeCompare(b));

/**
 * Chave usada nos paths `/webhook/set/{instance}`, `/webhook/find/{instance}`, etc.
 * Tenta `instanceName` / `name` no objeto ou dentro de `instance`; fallback `id` (alguns deploys).
 */
export function evolutionInstanceApiKey(row: unknown): string {
  if (!row || typeof row !== "object") return "";
  const o = row as Record<string, unknown>;
  const nested = o.instance;
  if (nested && typeof nested === "object") {
    const r = nested as Record<string, unknown>;
    const k = r.instanceName ?? r.name;
    if (typeof k === "string" && k.trim()) return k.trim();
    if (typeof r.id === "string" && r.id.trim()) return r.id.trim();
  }
  const name = o.instanceName ?? o.name;
  if (typeof name === "string" && name.trim()) return name.trim();
  if (typeof o.id === "string" && o.id.trim()) return o.id.trim();
  return "";
}

/** GET /webhook/find/{instance} — configuração atual do webhook na Evolution. */
export async function findEvolutionWebhook(
  instanceName: string,
  opts?: EvolutionPanelRequestOpts
): Promise<{
  ok: boolean;
  data?: {
    enabled: boolean;
    url: string;
    webhookByEvents: boolean;
    webhookBase64: boolean;
    events: string[];
  };
  error?: string;
  raw?: unknown;
}> {
  const parseWebhookPayload = (raw: unknown) => {
    const root = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
    const o =
      (root.webhook && typeof root.webhook === "object"
        ? (root.webhook as Record<string, unknown>)
        : root.data && typeof root.data === "object"
          ? (root.data as Record<string, unknown>)
          : root) || {};
    const nested =
      o.webhook && typeof o.webhook === "object"
        ? (o.webhook as Record<string, unknown>)
        : o;
    const eventsRaw = nested.events ?? nested.Events ?? o.events;
    const events = Array.isArray(eventsRaw)
      ? (eventsRaw as unknown[]).filter((e): e is string => typeof e === "string")
      : [];
    return {
      enabled: Boolean(nested.enabled ?? nested.Enabled ?? o.enabled),
      url: typeof nested.url === "string" ? nested.url : String(nested.url ?? o.url ?? ""),
      webhookByEvents: Boolean(
        nested.webhook_by_events ?? nested.webhookByEvents ?? nested.byEvents ?? false
      ),
      webhookBase64: Boolean(
        nested.webhook_base64 ?? nested.webhookBase64 ?? nested.base64 ?? true
      ),
      events,
    };
  };

  if (shouldUseEvolutionBackendProxy()) {
    try {
      const data = (await evolutionBackendRequest("GET", "/api/evolution/webhook", {
        params: { instance: instanceName },
        backendBaseUrl: backendBaseFromPanelOpts(opts),
      })) as { ok?: boolean; data?: unknown; error?: string };
      if (!data.ok) {
        return { ok: false, error: data.error || "Falha ao ler webhook via backend", raw: data };
      }
      return { ok: true, raw: data.data, data: parseWebhookPayload(data.data) };
    } catch (e) {
      if (!shouldUseEvolutionDirectFallback() || opts?.viewEnv) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : "Erro ao consultar webhook",
        };
      }
    }
  }

  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) {
    return { ok: false, error: "Credenciais da Evolution API não configuradas" };
  }
  try {
    const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
    const url = `${base}${EVOLUTION_CONFIG.ENDPOINTS.FIND_WEBHOOK}/${encodeURIComponent(instanceName)}`;
    const res = await fetch(url, { method: "GET", headers: getAuthHeaders() });
    const raw = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        error: parseEvolutionApiErrorResponse(raw, res.status),
        raw,
      };
    }
    return {
      ok: true,
      raw,
      data: parseWebhookPayload(raw),
    };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : "Erro ao consultar webhook",
    };
  }
}

/** POST /webhook/set/{instance} — grava webhook completo (painel dev). */
export async function setEvolutionWebhookCustom(
  instanceName: string,
  config: {
    enabled: boolean;
    url: string;
    webhookByEvents: boolean;
    webhookBase64: boolean;
    events: string[];
  },
  opts?: EvolutionPanelRequestOpts
): Promise<{ ok: boolean; error?: string }> {
  const extraHeaders = buildEvolutionWebhookDeliveryHeaders(config.url);
  const body = buildEvolutionWebhookSetPayload(
    config.enabled,
    config.url,
    config.webhookByEvents,
    config.webhookBase64,
    config.events,
    extraHeaders
  );

  if (shouldUseEvolutionBackendProxy()) {
    try {
      const data = (await evolutionBackendRequest("POST", "/api/evolution/webhook", {
        body: { instance: instanceName, ...body },
        backendBaseUrl: backendBaseFromPanelOpts(opts),
      })) as { ok?: boolean; error?: string };
      if (data.ok) return { ok: true };
      return {
        ok: false,
        error: safeUserFacingMessage(
          data.error,
          "Falha ao salvar webhook via backend"
        ),
      };
    } catch (e) {
      if (!shouldUseEvolutionDirectFallback() || opts?.viewEnv) {
        return {
          ok: false,
          error: safeUserFacingMessage(
            e instanceof Error ? e.message : null,
            "Erro ao salvar webhook"
          ),
        };
      }
    }
  }

  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) {
    return { ok: false, error: "Credenciais da Evolution API não configuradas" };
  }
  try {
    const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
    const path = `${EVOLUTION_CONFIG.ENDPOINTS.SET_WEBHOOK}/${encodeURIComponent(instanceName)}`;
    const res = await fetch(`${base}${path}`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        error: safeUserFacingMessage(
          parseEvolutionApiErrorResponse(data, res.status),
          "Erro ao salvar webhook"
        ),
      };
    }
    return { ok: true };
  } catch (error) {
    return {
      ok: false,
      error: safeUserFacingMessage(
        error instanceof Error ? error.message : null,
        "Erro ao salvar webhook"
      ),
    };
  }
}

function boolFromSettings(v: unknown): boolean {
  if (typeof v === "boolean") return v;
  if (v === "true" || v === 1) return true;
  if (v === "false" || v === 0) return false;
  return false;
}

/** GET /settings/find/{instance} */
export async function findEvolutionSettings(
  instanceName: string,
  opts?: EvolutionPanelRequestOpts
): Promise<{
  ok: boolean;
  data?: {
    rejectCall: boolean;
    groupsIgnore: boolean;
    alwaysOnline: boolean;
    readMessages: boolean;
    readStatus: boolean;
    syncFullHistory: boolean;
    msgCall?: string;
  };
  error?: string;
  raw?: unknown;
}> {
  const parseSettings = (raw: unknown) => {
    const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
    const nested =
      (o.settings as Record<string, unknown> | undefined) ||
      (o.data as Record<string, unknown> | undefined) ||
      o;
    const pick = (camel: string, snake: string) =>
      nested[camel] !== undefined ? nested[camel] : nested[snake];
    return {
      rejectCall: boolFromSettings(pick("rejectCall", "reject_call")),
      groupsIgnore: boolFromSettings(pick("groupsIgnore", "groups_ignore")),
      alwaysOnline: boolFromSettings(pick("alwaysOnline", "always_online")),
      readMessages: boolFromSettings(pick("readMessages", "read_messages")),
      readStatus: boolFromSettings(pick("readStatus", "read_status")),
      syncFullHistory: boolFromSettings(pick("syncFullHistory", "sync_full_history")),
      msgCall:
        typeof pick("msgCall", "msg_call") === "string"
          ? (pick("msgCall", "msg_call") as string)
          : undefined,
    };
  };

  if (shouldUseEvolutionBackendProxy()) {
    try {
      const data = (await evolutionBackendRequest("GET", "/api/evolution/settings", {
        params: { instance: instanceName },
        backendBaseUrl: backendBaseFromPanelOpts(opts),
      })) as { ok?: boolean; data?: unknown; error?: string };
      if (!data.ok) {
        return { ok: false, error: data.error || "Falha ao ler settings via backend", raw: data };
      }
      return { ok: true, raw: data.data, data: parseSettings(data.data) };
    } catch (e) {
      if (!shouldUseEvolutionDirectFallback() || opts?.viewEnv) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : "Erro ao consultar settings",
        };
      }
    }
  }

  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) {
    return { ok: false, error: "Credenciais da Evolution API não configuradas" };
  }
  try {
    const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
    const url = `${base}${EVOLUTION_CONFIG.ENDPOINTS.FIND_SETTINGS}/${encodeURIComponent(instanceName)}`;
    const res = await fetch(url, { method: "GET", headers: getAuthHeaders() });
    const raw = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        error: parseEvolutionApiErrorResponse(raw, res.status),
        raw,
      };
    }
    return { ok: true, raw, data: parseSettings(raw) };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : "Erro ao consultar settings",
    };
  }
}

/** POST /settings/set/{instance} — envia objeto camelCase esperado pela Evolution v2. */
export async function setEvolutionSettingsCustom(
  instanceName: string,
  body: {
    rejectCall: boolean;
    groupsIgnore: boolean;
    alwaysOnline: boolean;
    readMessages: boolean;
    readStatus: boolean;
    syncFullHistory: boolean;
    msgCall?: string;
  },
  opts?: EvolutionPanelRequestOpts
): Promise<{ ok: boolean; error?: string }> {
  const payload: Record<string, unknown> = {
    rejectCall: body.rejectCall,
    groupsIgnore: body.groupsIgnore,
    alwaysOnline: body.alwaysOnline,
    readMessages: body.readMessages,
    readStatus: body.readStatus,
    syncFullHistory: body.syncFullHistory,
    msgCall: body.msgCall ?? "",
  };

  if (shouldUseEvolutionBackendProxy()) {
    try {
      const data = (await evolutionBackendRequest("POST", "/api/evolution/settings", {
        body: { instance: instanceName, ...payload },
        backendBaseUrl: backendBaseFromPanelOpts(opts),
      })) as { ok?: boolean; error?: string };
      if (data.ok) return { ok: true };
      return { ok: false, error: data.error || "Falha ao salvar settings via backend" };
    } catch (e) {
      if (!shouldUseEvolutionDirectFallback() || opts?.viewEnv) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : "Erro ao salvar settings",
        };
      }
    }
  }

  if (!EVOLUTION_CONFIG.BASE_URL || !EVOLUTION_CONFIG.API_KEY) {
    return { ok: false, error: "Credenciais da Evolution API não configuradas" };
  }
  try {
    const base = EVOLUTION_CONFIG.BASE_URL.replace(/\/$/, "");
    const path = `${EVOLUTION_CONFIG.ENDPOINTS.SET_SETTINGS}/${encodeURIComponent(instanceName)}`;
    const res = await fetch(`${base}${path}`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        error: (data as { message?: string })?.message || `HTTP ${res.status}`,
      };
    }
    return { ok: true };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : "Erro ao salvar settings",
    };
  }
}

// Função para construir URL do Supabase
export function buildSupabaseUrl(table: string, action: string = "select") {
  return `${SUPABASE_CONFIG.URL}/rest/v1/${table}`;
}

// Função para hash de senha
export async function hashPassword(password: string): Promise<string> {
  // Use crypto API for better hashing if available
  if (window.crypto && window.crypto.subtle) {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(password + "delivai_salt_2024");
      const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      return hashHex;
    } catch (error) {
      console.warn("Crypto API falhou, usando base64:", error);
    }
  }

  // Fallback to base64 with salt
  return btoa(password + "delivai_salt_2024");
}

/** CNPJ fixo da empresa DelivAI (autenticação de colaboradores internos). */
export const DELIVAI_INTERNAL_CNPJ = "99999999000199";

/** Path do webhook n8n para GET/UPDATE de colaboradores (POST com `action`: get | update | new). */
export const N8N_WEBHOOK_COLABORADORES_PATH = "colaboradores";

/** Webhook POST: create/update/get em `caixas` (payload mastigado pelo front). */
export const N8N_WEBHOOK_MANAGE_CAIXA_PATH = "manage-caixa";

/** Se true, createPublicOrder não chama manage-caixa (útil até publicar o workflow no n8n). */
export const N8N_SKIP_CAIXA_RESOLUTION =
  process.env.REACT_APP_N8N_SKIP_CAIXA_RESOLUTION === "true";

/** Path do webhook n8n para CRUD em `prompt_templates` (POST com `action`: get | update | create | delete). */
export const N8N_WEBHOOK_PROMPT_TEMPLATES_PATH = "prompt-templates";

/**
 * POST n8n: corpo `{ dias, meio_cobranca, … }` — o workflow chama a API Santander e devolve PIX.
 * Credenciais Santander (client id/secret, certificado mTLS) vivem no n8n, não aqui — `REACT_APP_*`
 * são públicas no browser. Ver `n8n/santander-pix-credentials.example.env` e `santanderContratoPix.ts`.
 * Defina REACT_APP_N8N_WEBHOOK_SANTANDER_PIX_CONTRATO_PATH para o último segmento da URL (sem `/` inicial).
 */
export const N8N_WEBHOOK_SANTANDER_PIX_CONTRATO_PATH =
  (process.env.REACT_APP_N8N_WEBHOOK_SANTANDER_PIX_CONTRATO_PATH ||
    "santander-pix-contrato"
  ).trim();

/**
 * Webhook n8n Mercado Pago — criar preference (Checkout Pro).
 * Pré-requisito: workflow em n8n/mercadopago-licenca-checkout.json importado no n8n, Basic Auth no Webhook,
 * variáveis MERCADOPAGO_ACCESS_TOKEN (e em sandbox MERCADOPAGO_USE_SANDBOX_INIT=true) no ambiente n8n.
 * Ver documentation/pagamentos-delivai.txt e src/utils/mercadopagoLicencaPreference.ts.
 */
export const N8N_WEBHOOK_MERCADOPAGO_LICENCA_PATH = (
  process.env.REACT_APP_N8N_WEBHOOK_MERCADOPAGO_LICENCA_PATH || "mercadopago-licenca"
).trim();

/**
 * Origem pública do app para `back_urls` da Mercado Pago (deve ser absoluta: https://...).
 * A API costuma zerar `back_urls` se vier só path relativo. Use em builds onde `window.location.origin` não reflete o domínio público.
 */
export const SITE_ORIGIN = String(process.env.REACT_APP_SITE_ORIGIN || "")
  .trim()
  .replace(/\/+$/, "");

/** notification_url opcional enviada na preference (webhook MP → URL pública do n8n). */
export const MERCADOPAGO_LICENSE_NOTIFICATION_URL = (
  process.env.REACT_APP_MERCADOPAGO_LICENSE_NOTIFICATION_URL || ""
).trim();

/** Preço de referência exibido no front (R$/dia); valor cobrado deve vir do webhook / backend. */
export function getContratoPrecoPorDiaBrl(): number {
  const n = parseFloat(process.env.REACT_APP_CONTRATO_PRECO_POR_DIA_BRL || "12");
  return Number.isFinite(n) && n > 0 ? n : 12;
}

/** Limites do seletor de dias na tela de contrato. */
export const CONTRATO_DIAS_LIMITES = (() => {
  const mn = parseInt(process.env.REACT_APP_CONTRATO_DIAS_MIN || "1", 10);
  const mx = parseInt(process.env.REACT_APP_CONTRATO_DIAS_MAX || "90", 10);
  const min = Number.isFinite(mn) ? Math.min(365, Math.max(1, mn)) : 1;
  let max = Number.isFinite(mx) ? Math.min(365, Math.max(1, mx)) : 90;
  if (max < min) max = min;
  return { MIN: min, MAX: max };
})();

// Função para aplicar máscara de CNPJ
export function applyCnpjMask(value: string): string {
  value = value.replace(/\D/g, "").slice(0, 14);
  value = value.replace(/^(\d{2})(\d)/, "$1.$2");
  value = value.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3");
  value = value.replace(/\.(\d{3})(\d)/, ".$1/$2");
  value = value.replace(/(\d{4})(\d)/, "$1-$2");
  return value;
}

// Função para aplicar máscara de CPF
export type AccountKeyKind = "email" | "cpf" | "cnpj";

/** Um campo só: letra ou @ vira e-mail; até 11 dígitos, CPF; a partir do 12º, CNPJ. */
export function formatAccountKey(raw: string): {
  kind: AccountKeyKind;
  display: string;
  digits: string;
} {
  if (/[a-zA-Z@]/.test(raw)) {
    return { kind: "email", display: raw.replace(/\s/g, ""), digits: "" };
  }
  const only = raw.replace(/\D/g, "").slice(0, 14);
  if (only.length <= 11) {
    return { kind: "cpf", display: applyCpfMask(only), digits: only };
  }
  return { kind: "cnpj", display: applyCnpjMask(only), digits: only };
}

export function applyCpfMask(value: string): string {
  value = value.replace(/\D/g, "").slice(0, 11);
  value = value.replace(/^(\d{3})(\d)/, "$1.$2");
  value = value.replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3");
  value = value.replace(/\.(\d{3})(\d)/, ".$1-$2");
  return value;
}

// Função para aplicar máscara de telefone
export function applyPhoneMask(value: string): string {
  value = value.replace(/\D/g, "").slice(0, 11);
  if (value.length <= 10) {
    value = value.replace(/^(\d{2})(\d)/, "($1) $2");
    value = value.replace(/(\d{4})(\d)/, "$1-$2");
  } else {
    value = value.replace(/^(\d{2})(\d)/, "($1) $2");
    value = value.replace(/(\d{5})(\d)/, "$1-$2");
  }
  return value;
}

// Função para aplicar máscara de CEP
export function applyCepMask(value: string): string {
  value = value.replace(/\D/g, "").slice(0, 8);
  value = value.replace(/^(\d{5})(\d)/, "$1-$2");
  return value;
}

// Função para limpar formatação de CNPJ
export function cleanCnpjFormat(value: string): string {
  return value.replace(/\D/g, "");
}

export function cleanCpfFormat(value: string): string {
  return value.replace(/\D/g, "");
}

// Função para limpar formatação de telefone
export function cleanPhoneFormat(value: string): string {
  return value.replace(/\D/g, "");
}

// Função para limpar formatação de CEP
export function cleanCepFormat(value: string): string {
  return value.replace(/\D/g, "");
}

// Função para validar CNPJ seguindo as regras oficiais
export function validateCnpj(cnpj: string): boolean {
  // Limpar o CNPJ (remover pontos, traços e barras)
  const cleanCnpj = cleanCnpjFormat(cnpj);

  // Verificar se tem 14 dígitos
  if (cleanCnpj.length !== 14) {
    return false;
  }

  // EXCEÇÃO: CNPJ da empresa mãe (DelivAI interno) - sempre válido
  if (cleanCnpj === DELIVAI_INTERNAL_CNPJ) {
    return true;
  }

  // Verificar se todos os dígitos são iguais (sequência repetida)
  if (/^(\d)\1+$/.test(cleanCnpj)) {
    return false;
  }

  // Calcular primeiro dígito verificador (13º dígito)
  // Pesos: 5,4,3,2,9,8,7,6,5,4,3,2 (da esquerda para direita)
  const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;

  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleanCnpj[i]) * pesos1[i];
  }

  const resto1 = sum % 11;
  const digit1 = resto1 < 2 ? 0 : 11 - resto1;

  // Calcular segundo dígito verificador (14º dígito)
  // Pesos: 6,5,4,3,2,9,8,7,6,5,4,3,2 (incluindo o primeiro dígito verificador)
  const pesos2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  sum = 0;

  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleanCnpj[i]) * pesos2[i];
  }

  const resto2 = sum % 11;
  const digit2 = resto2 < 2 ? 0 : 11 - resto2;

  // Verificar se os dígitos verificadores estão corretos
  return (
    parseInt(cleanCnpj[12]) === digit1 && parseInt(cleanCnpj[13]) === digit2
  );
}

export function validateCpf(cpf: string): boolean {
  const clean = cleanCpfFormat(cpf);
  if (clean.length !== 11) return false;
  if (/^(\d)\1+$/.test(clean)) return false;

  const calcDigit = (base: string, weights: number[]) => {
    let sum = 0;
    for (let i = 0; i < base.length; i++) {
      sum += parseInt(base[i], 10) * weights[i];
    }
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };

  const d1 = calcDigit(clean.slice(0, 9), [10, 9, 8, 7, 6, 5, 4, 3, 2]);
  const d2 = calcDigit(clean.slice(0, 10), [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]);
  return d1 === parseInt(clean[9], 10) && d2 === parseInt(clean[10], 10);
}

// Função para validar email
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Função para validar telefone
export function validatePhone(phone: string): boolean {
  const cleanPhone = cleanPhoneFormat(phone);
  return cleanPhone.length >= 10 && cleanPhone.length <= 11;
}

// Função para validar CEP
export function validateCep(cep: string): boolean {
  const cleanCep = cleanCepFormat(cep);
  return cleanCep.length === 8;
}

// Função para verificar se usuário está logado
export function isLoggedIn(): boolean {
  const token = localStorage.getItem("evolution_token");
  const user = localStorage.getItem("evolution_user");
  return !!(token && user);
}

// Função para obter usuário atual
export function getCurrentUser(): User | null {
  try {
    // Primeiro tentar buscar do localStorage correto (delivai_user)
    const delivaiUser = localStorage.getItem("delivai_user");
    if (delivaiUser) {
      const user = JSON.parse(delivaiUser);
      if (user?.cnpj) return user;
    }

    // Fallback para evolution_user (se existir)
    const evolutionUser = localStorage.getItem("evolution_user");
    if (evolutionUser) {
      const user = JSON.parse(evolutionUser);
      if (user?.cnpj) return user;
    }

    // Fallback para CNPJ direto no localStorage
    const cnpj = localStorage.getItem("cnpj");
    if (cnpj) {
      // Criar um usuário básico com o CNPJ
      return {
        id: cnpj,
        name: "Empresa",
        cnpj: cnpj,
        email: "",
        token: "local_token_" + cnpj
      };
    }

    console.warn("🔍 getCurrentUser: Nenhum usuário ou CNPJ encontrado no localStorage");
    return null;
  } catch (error) {
    console.error("🔍 getCurrentUser: Erro ao buscar usuário do localStorage:", error);
    return null;
  }
}

// Função para fazer logout
export function evolutionLogout(): void {
  localStorage.removeItem("evolution_token");
  localStorage.removeItem("evolution_user");
  localStorage.removeItem("delivai_user");
}

// Variáveis de ambiente para n8n
export const N8N_BASE_URL_EXPORT = N8N_BASE_URL;
export const N8N_BASIC_AUTH_EXPORT = N8N_BASIC_AUTH;
